import React, { useState, useEffect, useContext, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Server, Plus, Search, Edit, Trash2, Eye, EyeOff,
  X, Save, Monitor, Wifi, HardDrive, Globe, Key, Clock,
  Building2, Copy, Settings, Lock, History,
  ArrowUpDown, ChevronUp, ChevronDown, Check, Laptop, Link2,
  ToggleRight, ToggleLeft
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { formatMoneyInput, normalizeMoneyValue, parseMoneyInput } from "../components/ui/MoneyInput";
import { AuditoriaRegistroBtn } from "../components/AuditoriaModal";
import { AuthContext } from "../App";
import { toast } from "sonner";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMontoAdq = (n, moneda) => {
  if (n == null || isNaN(n)) return "-";
  if (moneda === "USD") return "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (moneda === "BRL") return "R$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return "₲ " + Math.round(n).toLocaleString("es-PY");
};

const categoriaIconMap = {
  "Servidores": <HardDrive className="w-5 h-5" />,
  "Dispositivos": <Monitor className="w-5 h-5" />,
  "Cuentas de Acceso": <Key className="w-5 h-5" />,
  "Dominios y Servicios": <Globe className="w-5 h-5" />,
};

const estadoColors = {
  "activo": "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  "inactivo": "bg-red-500/20 text-red-400 border-red-500/30",
  "mantenimiento": "bg-amber-500/20 text-amber-400 border-amber-500/30",
};

const ipToNumber = (ip) => {
  if (!ip) return -1;

  const parts = String(ip).trim().split(".");
  if (parts.length !== 4) return -1;

  const nums = parts.map(p => Number(p));
  if (nums.some(n => Number.isNaN(n) || n < 0 || n > 255)) return -1;

  return nums.reduce((acc, oct) => acc * 256 + oct, 0);
};

function ChipSearch({ chips, setChips, inputVal, setInputVal, placeholder }) {
  const addChip = () => {
    const term = inputVal.trim().replace(/,$/, "");
    if (term && !chips.includes(term)) setChips(prev => [...prev, term]);
    setInputVal("");
  };
  return (
    <div className="space-y-2 flex-1 min-w-[260px]">
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {chips.map((chip, idx) => (
            <span key={chip} className="inline-flex items-center gap-1.5 bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 rounded-full px-2.5 py-1 text-xs">
              <Search className="w-3 h-3" />
              {chip}
              <button type="button" onClick={() => setChips(prev => prev.filter((_, i) => i !== idx))} className="hover:text-white">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button type="button" onClick={() => setChips([])} className="text-xs text-slate-500 hover:text-slate-300 px-2 py-1 rounded-full border border-white/10">
            Limpiar
          </button>
        </div>
      )}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          type="text"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === ",") && inputVal.trim()) { e.preventDefault(); addChip(); }
            if (e.key === "Backspace" && !inputVal && chips.length) setChips(prev => prev.slice(0, -1));
          }}
          placeholder={placeholder}
          className="w-full bg-arandu-dark border border-white/10 rounded-lg pl-10 pr-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/60"
          data-testid="inv-search"
        />
      </div>
    </div>
  );
}

function matchChips(chips, inputVal, texto) {
  const terms = [...chips, ...(inputVal.trim() ? [inputVal.trim()] : [])];
  if (!terms.length) return true;
  const haystack = texto.toLowerCase();
  return terms.every(term => haystack.includes(term.toLowerCase()));
}

// Campos personalizados por subtipo de Cuenta de Acceso
const CAMPOS_CUENTA = {
  "AnyDesk": [
    { key: "id_anydesk", label: "ID AnyDesk", placeholder: "123 456 789" },
    { key: "alias", label: "Alias", placeholder: "PC-Recepcion" },
    { key: "contrasena", label: "Contrasena", placeholder: "***", isPassword: true },
  ],
  "Office 365": [
    { key: "tenant", label: "Tenant", placeholder: "empresa.onmicrosoft.com" },
    { key: "admin_email", label: "Email Admin", placeholder: "admin@empresa.com" },
    { key: "contrasena", label: "Contrasena", placeholder: "***", isPassword: true },
    { key: "licencias", label: "Licencias", placeholder: "Business Basic x5" },
  ],
  "Correo": [
    { key: "servidor", label: "Servidor", placeholder: "mail.empresa.com" },
    { key: "correo", label: "Correo", placeholder: "usuario@empresa.com" },
    { key: "contrasena", label: "Contrasena", placeholder: "***", isPassword: true },
    { key: "protocolo", label: "Protocolo", placeholder: "IMAP / POP3 / SMTP" },
    { key: "puerto", label: "Puerto", placeholder: "993" },
  ],
  "Zimbra": [
    { key: "servidor", label: "Servidor", placeholder: "zimbra.empresa.com" },
    { key: "dominio", label: "Dominio", placeholder: "empresa.com" },
    { key: "admin", label: "Admin", placeholder: "admin@empresa.com" },
    { key: "contrasena", label: "Contrasena", placeholder: "***", isPassword: true },
    { key: "puerto_web", label: "Puerto Admin", placeholder: "7071" },
  ],
  "SSH": [
    { key: "host", label: "Host", placeholder: "192.168.1.1" },
    { key: "puerto", label: "Puerto", placeholder: "22" },
    { key: "usuario", label: "Usuario", placeholder: "root" },
    { key: "contrasena", label: "Contrasena", placeholder: "***", isPassword: true },
  ],
  "RDP": [
    { key: "host", label: "Host", placeholder: "192.168.1.100" },
    { key: "puerto", label: "Puerto", placeholder: "3389" },
    { key: "usuario", label: "Usuario", placeholder: "administrador" },
    { key: "contrasena", label: "Contrasena", placeholder: "***", isPassword: true },
  ],
  "VPN": [
    { key: "servidor", label: "Servidor", placeholder: "vpn.empresa.com" },
    { key: "tipo", label: "Tipo VPN", placeholder: "L2TP / OpenVPN / WireGuard" },
    { key: "usuario", label: "Usuario", placeholder: "user" },
    { key: "contrasena", label: "Contrasena", placeholder: "***", isPassword: true },
    { key: "secreto", label: "Secreto/PSK", placeholder: "***", isPassword: true },
  ],
  "WiFi": [
    { key: "ssid", label: "SSID (Nombre Red)", placeholder: "Empresa-WiFi" },
    { key: "contrasena", label: "Contrasena", placeholder: "***", isPassword: true },
    { key: "seguridad", label: "Seguridad", placeholder: "WPA2 / WPA3" },
  ],
  "Panel Web": [
    { key: "url", label: "URL", placeholder: "https://panel.empresa.com" },
    { key: "usuario", label: "Usuario", placeholder: "admin" },
    { key: "contrasena", label: "Contrasena", placeholder: "***", isPassword: true },
  ],
  "Base de Datos": [
    { key: "host", label: "Host", placeholder: "localhost" },
    { key: "puerto", label: "Puerto", placeholder: "3306" },
    { key: "nombre_bd", label: "Nombre BD", placeholder: "produccion_db" },
    { key: "usuario", label: "Usuario", placeholder: "root" },
    { key: "contrasena", label: "Contrasena", placeholder: "***", isPassword: true },
  ],
  "Winbox": [
    { key: "host", label: "Host / IP", placeholder: "192.168.88.1" },
    { key: "usuario", label: "Usuario", placeholder: "admin" },
    { key: "contrasena", label: "Contrasena", placeholder: "***", isPassword: true },
  ],
  "FTP": [
    { key: "host", label: "Host", placeholder: "ftp.empresa.com" },
    { key: "puerto", label: "Puerto", placeholder: "21" },
    { key: "usuario", label: "Usuario", placeholder: "ftpuser" },
    { key: "contrasena", label: "Contrasena", placeholder: "***", isPassword: true },
  ],
  "Personalizada": [
    { key: "campo1_nombre", label: "Campo 1 - Nombre", placeholder: "Ej: Servidor" },
    { key: "campo1_valor", label: "Campo 1 - Valor", placeholder: "Ej: 192.168.1.1" },
    { key: "campo2_nombre", label: "Campo 2 - Nombre", placeholder: "Ej: Usuario" },
    { key: "campo2_valor", label: "Campo 2 - Valor", placeholder: "Ej: admin" },
    { key: "campo3_nombre", label: "Campo 3 - Nombre", placeholder: "Ej: Contrasena" },
    { key: "campo3_valor", label: "Campo 3 - Valor", placeholder: "***", isPassword: true },
  ],
};

const InventarioPage = () => {
  const { token, user, hasPermission, activeEmpresaPropia } = useContext(AuthContext);
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const catQs = activeEmpresaPropia?.slug ? `?logo_tipo=${encodeURIComponent(activeEmpresaPropia.slug)}` : "";
  const catQsAnd = activeEmpresaPropia?.slug ? `&logo_tipo=${encodeURIComponent(activeEmpresaPropia.slug)}` : "";
  const canCreate = hasPermission("inventario.crear");
  const canEdit = hasPermission("inventario.editar");
  const canDelete = hasPermission("inventario.eliminar");
  const [searchParams, setSearchParams] = useSearchParams();
  const empresaFromUrl = searchParams.get("empresa") || "";
  const nuevoFromUrl = searchParams.get("nuevo") || "";

  const [activos, setActivos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterEmpresa, setFilterEmpresa] = useState(empresaFromUrl);
  const [filterCategoria, setFilterCategoria] = useState("");
  const [filterEstado, setFilterEstado] = useState("");
  const [searchChips, setSearchChips] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedInput, setDebouncedInput] = useState("");
  const [visibleCount, setVisibleCount] = useState(200);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedInput(searchInput), 250);
    return () => clearTimeout(t);
  }, [searchInput]);
  useEffect(() => { setVisibleCount(200); }, [searchChips, debouncedInput, filterEmpresa]); // eslint-disable-line
  const [sortBy, setSortBy] = useState("nombre");
  const [sortDir, setSortDir] = useState("asc");
  const [viewMode, setViewMode] = useState("lista"); // "lista" | "por_dispositivo" | "ip_agrupado"

  const [showForm, setShowForm] = useState(false);
  const [editingActivo, setEditingActivo] = useState(null);
  const [showCredenciales, setShowCredenciales] = useState(null);
  const [showHistorial, setShowHistorial] = useState(null);
  const [showCategorias, setShowCategorias] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [detailPasswords, setDetailPasswords] = useState({});
  const [detailCredenciales, setDetailCredenciales] = useState([]);
  const [detailCredLoading, setDetailCredLoading] = useState(false);

  const [credenciales, setCredenciales] = useState([]);
  const [showPasswords, setShowPasswords] = useState({});
  const [credForm, setCredForm] = useState(null);
  // Credenciales a crear junto con el activo nuevo
  const emptyCredInicial = { tipo_acceso: "", usuario: "", password: "", url_acceso: "", observaciones: "" };
  const [credencialesIniciales, setCredencialesIniciales] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [showFieldPasswords, setShowFieldPasswords] = useState({});

  const emptyForm = {
    empresa_id: "", categoria: "", subtipo: "", nombre: "", descripcion: "",
    ubicacion: "", ip_local: "", ip_publica: "", ips_locales: [], ips_publicas: [],
    dominio: "", puerto_local: "", puerto_externo: "", version: "", estado: "activo",
    ultima_revision: "", observaciones: "",
    campos_personalizados: {}, activos_asignados: [], nvr_dvr_id: null,
    // Hardware — PC Escritorio / Notebook
    mac_address: "", procesador: "", ram: "", disco_duro: "",
    marca_monitor: "", nombre_equipo: "", usuario_actual: "", departamento: "", cuenta_office: "",
    // Datos del bien — adquisición
    marca: "", modelo: "", chasis: "", numero_factura: "",
    fecha_adquisicion: "", monto_adquisicion: "", moneda_adquisicion: "PYG", anio_fabricacion: "", estado_bien: "normal",
  };

  const [formData, setFormData] = useState(emptyForm);
  const [newIpLocal, setNewIpLocal] = useState("");
  const [newIpPublica, setNewIpPublica] = useState("");
  const [editingIp, setEditingIp] = useState(null); // { type: "local"|"publica", index, value }

  const [catForm, setCatForm] = useState({ nombre: "", subtipos: "", predeterminada: false });
  const [categoriasEliminadas, setCategoriasEliminadas] = useState([]);
  const [plantillasCat, setPlantillasCat] = useState([]);
  const [showPlantillas, setShowPlantillas] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [newSubtipo, setNewSubtipo] = useState("");
  const [saving, setSaving] = useState(false);
  const [duplicatingFromId, setDuplicatingFromId] = useState(null);
  const [expandedNvrGroups, setExpandedNvrGroups] = useState(new Set());
  // Configuración: permitir nombres de activo duplicados (desactivado por defecto)
  const [activeTab, setActiveTab] = useState("activos"); // "activos" | "configuracion"
  const [invConfig, setInvConfig] = useState({ permitir_nombres_duplicados: false });
  const [savingInvConfig, setSavingInvConfig] = useState(false);
  const canVerConfigInv = hasPermission("configuracion_inventario.ver");
  const canGestionarUnicidad = hasPermission("configuracion_inventario.gestionar_unicidad");
  const canVerConfigTab = canVerConfigInv || canGestionarUnicidad;

  useEffect(() => { fetchAll(); }, [activeEmpresaPropia]); // eslint-disable-line

	  useEffect(() => {
	    if (filterEmpresa && empresas.length && !empresas.some(e => e.id === filterEmpresa)) {
	      setFilterEmpresa("");
	    }
	    if (filterEmpresa && empresas.length) {
	      const emp = empresas.find(e => e.id === filterEmpresa);
	      if (emp?.nombre) setSearchChips(prev => prev.includes(emp.nombre) ? prev : [...prev, emp.nombre]);
	    }
	  }, [empresas, filterEmpresa]);

  const fetchAll = async () => {
    try {
      const params = new URLSearchParams();
      if (activeEmpresaPropia?.slug) params.set("logo_tipo", activeEmpresaPropia.slug);
      const qs = params.toString();
      const [actRes, empRes, catRes] = await Promise.all([
        fetch(`${API}/admin/activos${qs ? `?${qs}` : ""}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/entidades?es_cliente=true${activeEmpresaPropia?.slug ? `&logo_tipo=${activeEmpresaPropia.slug}` : ""}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/categorias${catQs}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (actRes.ok) setActivos(await actRes.json());
      if (empRes.ok) setEmpresas(await empRes.json());
      if (catRes.ok) setCategorias(await catRes.json());
      if (canVerConfigInv || canGestionarUnicidad) {
        const cfgRes = await fetch(`${API}/admin/config-inventario${activeEmpresaPropia?.slug ? `?logo_tipo=${activeEmpresaPropia.slug}` : ""}`, { headers: { Authorization: `Bearer ${token}` } });
        if (cfgRes.ok) setInvConfig(await cfgRes.json());
      }
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const isCuenta = (cat) => cat === "Cuentas de Acceso";

const activosIndexados = useMemo(() => activos.map(a => ({
  a,
  texto: [
    a.nombre, a.ip_local, a.ip_publica, ...(a.ips_locales || []), ...(a.ips_publicas || []),
    a.dominio, a.descripcion, a.ubicacion, a.empresa_nombre, a.categoria, a.subtipo,
    a.estado, ...Object.values(a.campos_personalizados || {})
  ].filter(Boolean).join(" ").toLowerCase(),
})), [activos]);

const filteredActivos = useMemo(() => {
  const terms = [...searchChips, ...(debouncedInput.trim() ? [debouncedInput.trim()] : [])].map(t => t.toLowerCase());
  // Sin filtro (empresa/cliente u otro término) no se muestra nada
  if (terms.length === 0) return [];
  let result = activosIndexados
    .filter(({ texto }) => terms.every(t => texto.includes(t)))
    .map(({ a }) => a);

  result.sort((a, b) => {
    if (sortBy === "ip") {
      const ipA = a.ip_local || (a.ips_locales?.[0] || "");
      const ipB = b.ip_local || (b.ips_locales?.[0] || "");

      const cmp = ipToNumber(ipA) - ipToNumber(ipB);
      return sortDir === "asc" ? cmp : -cmp;
    }

    let va, vb;
    if (sortBy === "nombre") { va = a.nombre; vb = b.nombre; }
    else if (sortBy === "fecha") { va = a.created_at; vb = b.created_at; }
    else if (sortBy === "categoria") { va = a.categoria; vb = b.categoria; }
    else if (sortBy === "empresa") { va = a.empresa_nombre; vb = b.empresa_nombre; }
    else { va = a.nombre; vb = b.nombre; }

    const cmp = String(va || "").localeCompare(String(vb || ""), "es", { sensitivity: "base" });
    return sortDir === "asc" ? cmp : -cmp;
  });

  return result;
	}, [activosIndexados, searchChips, debouncedInput, sortBy, sortDir]);

  const groupedByCategoria = useMemo(() => {
    const groups = {};
    filteredActivos.forEach(a => {
      const catName = a.categoria || "Sin Categoria";
      if (!groups[catName]) groups[catName] = [];
      groups[catName].push(a);
    });
    return groups;
  }, [filteredActivos]);

  // Empresa de contexto: si hay filterEmpresa por URL, o si los chips coinciden con exactamente una empresa
  const contextEmpresaId = useMemo(() => {
    if (filterEmpresa) return filterEmpresa;
    if (searchChips.length > 0 && empresas.length > 0) {
      const chipsLower = searchChips.map(c => c.toLowerCase());
      const matched = empresas.filter(e =>
        chipsLower.some(chip => e.nombre.toLowerCase().includes(chip))
      );
      if (matched.length === 1) return matched[0].id;
    }
    return null;
  }, [filterEmpresa, searchChips, empresas]);

  // Agrupación por NVR/DVR para los modos "por_dispositivo" e "ip_agrupado"
  const nvrGroups = useMemo(() => {
    // Función de comparación según el campo de sort activo
    const cmpItems = (a, b) => {
      let cmp = 0;
      if (sortBy === "ip") {
        const ia = a.ip_local || (a.ips_locales?.[0] || "");
        const ib = b.ip_local || (b.ips_locales?.[0] || "");
        cmp = ipToNumber(ia) - ipToNumber(ib);
      } else if (sortBy === "empresa") {
        cmp = String(a.empresa_nombre || "").localeCompare(String(b.empresa_nombre || ""), "es", { sensitivity: "base" });
      } else if (sortBy === "categoria") {
        cmp = String(a.categoria || "").localeCompare(String(b.categoria || ""), "es", { sensitivity: "base" });
      } else {
        cmp = String(a.nombre || "").localeCompare(String(b.nombre || ""), "es", { sensitivity: "base" });
      }
      return sortDir === "asc" ? cmp : -cmp;
    };

    const nvrDvrItems = filteredActivos.filter(a =>
      a.categoria === "Dispositivos" && (a.subtipo === "NVR" || a.subtipo === "DVR")
    );
    const camerasWithParent = filteredActivos.filter(a => a.nvr_dvr_id);
    const nvrDvrIdsWithCameras = new Set(camerasWithParent.map(c => c.nvr_dvr_id));
    const standaloneItems = filteredActivos.filter(a =>
      !a.nvr_dvr_id && !(a.categoria === "Dispositivos" && (a.subtipo === "NVR" || a.subtipo === "DVR") && nvrDvrIdsWithCameras.has(a.id))
    );
    const nvrDvrWithCameras = nvrDvrItems.filter(a => nvrDvrIdsWithCameras.has(a.id));

    const groups = nvrDvrWithCameras.map(nvr => {
      const cameras = [...camerasWithParent.filter(c => c.nvr_dvr_id === nvr.id)].sort(cmpItems);
      return { nvr, cameras };
    });

    groups.sort((a, b) => cmpItems(a.nvr, b.nvr));

    return { groups, standaloneItems: [...standaloneItems].sort(cmpItems) };
  }, [filteredActivos, sortBy, sortDir]);

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortDir("asc"); }
  };

  // CRUD
  const openNewForm = () => {
    setEditingActivo(null);
    setDuplicatingFromId(null);
    setFormData({ ...emptyForm, empresa_id: contextEmpresaId || "" });
    setNewIpLocal(""); setNewIpPublica(""); setEditingIp(null);
    setShowFieldPasswords({});
    setCredencialesIniciales([]);
    setShowForm(true);
  };

  useEffect(() => {
    if (nuevoFromUrl !== "dispositivo") return;
    if (canCreate) openNewForm();
    const next = new URLSearchParams(searchParams);
    next.delete("nuevo");
    setSearchParams(next, { replace: true });
  }, [nuevoFromUrl]); // eslint-disable-line

  const openEditForm = (activo) => {
    setEditingActivo(activo);
    setDuplicatingFromId(null);
    setFormData({
      empresa_id: activo.empresa_id, categoria: activo.categoria, subtipo: activo.subtipo || "",
      nombre: activo.nombre, descripcion: activo.descripcion || "",
      ubicacion: activo.ubicacion || "", ip_local: activo.ip_local || "",
      ip_publica: activo.ip_publica || "",
      ips_locales: activo.ips_locales || [], ips_publicas: activo.ips_publicas || [],
      dominio: activo.dominio || "", puerto_local: activo.puerto_local || "",
      puerto_externo: activo.puerto_externo || "", version: activo.version || "",
      estado: activo.estado || "activo", ultima_revision: activo.ultima_revision || "",
      observaciones: activo.observaciones || "",
      campos_personalizados: activo.campos_personalizados || {},
      activos_asignados: activo.activos_asignados || [],
      nvr_dvr_id: activo.nvr_dvr_id || null,
      mac_address: activo.mac_address || "", procesador: activo.procesador || "",
      ram: activo.ram || "", disco_duro: activo.disco_duro || "",
      marca_monitor: activo.marca_monitor || "", nombre_equipo: activo.nombre_equipo || "",
      usuario_actual: activo.usuario_actual || "", departamento: activo.departamento || "",
      cuenta_office: activo.cuenta_office || "",
      marca: activo.marca || "", modelo: activo.modelo || "", chasis: activo.chasis || "",
      numero_factura: activo.numero_factura || "", fecha_adquisicion: activo.fecha_adquisicion || "",
      monto_adquisicion: activo.monto_adquisicion != null ? String(activo.monto_adquisicion) : "",
      moneda_adquisicion: activo.moneda_adquisicion || "PYG",
      anio_fabricacion: activo.anio_fabricacion != null ? String(activo.anio_fabricacion) : "",
      estado_bien: activo.estado_bien || "normal",
    });
    setNewIpLocal(""); setNewIpPublica(""); setEditingIp(null);
    setShowFieldPasswords({});
    setShowForm(true);
  };

  // Duplicar activo — copia todos los datos incluyendo credenciales
  const duplicateActivo = (activo) => {
    setEditingActivo(null);
    setDuplicatingFromId(activo.id);
    setFormData({
      empresa_id: activo.empresa_id, categoria: activo.categoria, subtipo: activo.subtipo || "",
      nombre: activo.nombre, descripcion: activo.descripcion || "",
      ubicacion: activo.ubicacion || "", ip_local: activo.ip_local || "",
      ip_publica: activo.ip_publica || "",
      ips_locales: [...(activo.ips_locales || [])], ips_publicas: [...(activo.ips_publicas || [])],
      dominio: activo.dominio || "", puerto_local: activo.puerto_local || "",
      puerto_externo: activo.puerto_externo || "", version: activo.version || "",
      estado: activo.estado || "activo", ultima_revision: "",
      observaciones: activo.observaciones || "",
      campos_personalizados: { ...(activo.campos_personalizados || {}) },
      activos_asignados: [],
      nvr_dvr_id: activo.nvr_dvr_id || null,
      mac_address: "", procesador: activo.procesador || "",
      ram: activo.ram || "", disco_duro: activo.disco_duro || "",
      marca_monitor: activo.marca_monitor || "", nombre_equipo: "",
      usuario_actual: activo.usuario_actual || "", departamento: activo.departamento || "",
      cuenta_office: activo.cuenta_office || "",
      // Chasis y N° de factura son únicos por unidad física — no se duplican
      marca: activo.marca || "", modelo: activo.modelo || "", chasis: "",
      numero_factura: "", fecha_adquisicion: activo.fecha_adquisicion || "",
      monto_adquisicion: activo.monto_adquisicion != null ? String(activo.monto_adquisicion) : "",
      moneda_adquisicion: activo.moneda_adquisicion || "PYG",
      anio_fabricacion: activo.anio_fabricacion != null ? String(activo.anio_fabricacion) : "",
      estado_bien: activo.estado_bien || "normal",
    });
    setNewIpLocal(""); setNewIpPublica(""); setEditingIp(null);
    setShowFieldPasswords({});
    setShowForm(true);
  };

  const addIp = (type) => {
    if (type === "local" && newIpLocal.trim()) {
      if (formData.ips_locales.map(ip => ip.trim().toLowerCase()).includes(newIpLocal.trim().toLowerCase())) {
        toast.error(`La IP ${newIpLocal.trim()} ya está cargada en este activo`);
        return;
      }
      setFormData(f => ({ ...f, ips_locales: [...f.ips_locales, newIpLocal.trim()] }));
      setNewIpLocal("");
    } else if (type === "publica" && newIpPublica.trim()) {
      if (formData.ips_publicas.map(ip => ip.trim().toLowerCase()).includes(newIpPublica.trim().toLowerCase())) {
        toast.error(`La IP ${newIpPublica.trim()} ya está cargada en este activo`);
        return;
      }
      setFormData(f => ({ ...f, ips_publicas: [...f.ips_publicas, newIpPublica.trim()] }));
      setNewIpPublica("");
    }
  };

  const removeIp = (type, idx) => {
    if (type === "local") setFormData(f => ({ ...f, ips_locales: f.ips_locales.filter((_, i) => i !== idx) }));
    else setFormData(f => ({ ...f, ips_publicas: f.ips_publicas.filter((_, i) => i !== idx) }));
  };

  const startEditIp = (type, index) => {
    const current = type === "local" ? formData.ips_locales[index] : formData.ips_publicas[index];
    setEditingIp({ type, index, value: current });
  };

  const confirmEditIp = () => {
    if (!editingIp) return;
    const { type, index, value } = editingIp;
    const trimmed = value.trim();
    if (trimmed) {
      if (type === "local") {
        setFormData(f => {
          const updated = [...f.ips_locales];
          updated[index] = trimmed;
          return { ...f, ips_locales: updated };
        });
      } else {
        setFormData(f => {
          const updated = [...f.ips_publicas];
          updated[index] = trimmed;
          return { ...f, ips_publicas: updated };
        });
      }
    }
    setEditingIp(null);
  };

  const updateCampo = (key, value) => {
    setFormData(f => ({ ...f, campos_personalizados: { ...f.campos_personalizados, [key]: value } }));
  };

  const toggleActivoAsignado = (activoId) => {
    setFormData(f => ({
      ...f,
      activos_asignados: f.activos_asignados.includes(activoId)
        ? f.activos_asignados.filter(id => id !== activoId)
        : [...f.activos_asignados, activoId]
    }));
  };

  const saveActivo = async () => {
    if (!formData.empresa_id || !formData.categoria || !formData.nombre) {
      toast.error("Empresa, categoria y nombre son obligatorios"); return;
    }
    // Bloquear si ya existe un activo con el mismo nombre en la misma empresa —
    // salvo que la Configuración de Inventario tenga habilitados los nombres
    // duplicados (desactivado por defecto; el backend también lo valida).
    if (!invConfig.permitir_nombres_duplicados) {
      const duplicate = activos.find(a =>
        a.nombre?.trim().toLowerCase() === formData.nombre.trim().toLowerCase() &&
        a.empresa_id === formData.empresa_id &&
        a.id !== editingActivo?.id
      );
      if (duplicate) {
        toast.error(`Ya existe "${formData.nombre}" en esta empresa. Cambia el nombre para continuar.`);
        return;
      }
    }
    // Si el usuario escribió una IP pero no presionó Enter, incluirla igual al guardar
    const dataToSave = { ...formData };
    // Normalizar monto/año de adquisición a numérico (o null) antes de enviar
    const montoAdqParsed = parseMoneyInput(dataToSave.monto_adquisicion, dataToSave.moneda_adquisicion);
    dataToSave.monto_adquisicion = Number.isNaN(montoAdqParsed) ? null : montoAdqParsed;
    const anioParsed = parseInt(dataToSave.anio_fabricacion, 10);
    dataToSave.anio_fabricacion = Number.isNaN(anioParsed) ? null : anioParsed;
    if (newIpLocal.trim()) {
      dataToSave.ips_locales = [...dataToSave.ips_locales, newIpLocal.trim()];
      setNewIpLocal("");
    }
    if (newIpPublica.trim()) {
      dataToSave.ips_publicas = [...dataToSave.ips_publicas, newIpPublica.trim()];
      setNewIpPublica("");
    }
    const ipsToSave = [
      dataToSave.ip_local, dataToSave.ip_publica,
      ...(dataToSave.ips_locales || []), ...(dataToSave.ips_publicas || [])
    ].map(ip => String(ip || "").trim().toLowerCase()).filter(Boolean);
    const repeatedInForm = [...new Set(ipsToSave)].find(ip => ipsToSave.filter(x => x === ip).length > 1);
    if (repeatedInForm) {
      toast.error(`La IP ${repeatedInForm} está repetida en este activo`);
      return;
    }
    const duplicateIp = activos.find(a => {
      if (a.id === editingActivo?.id || a.empresa_id !== dataToSave.empresa_id) return false;
      const ips = [a.ip_local, a.ip_publica, ...(a.ips_locales || []), ...(a.ips_publicas || [])]
        .map(ip => String(ip || "").trim().toLowerCase()).filter(Boolean);
      return ips.some(ip => ipsToSave.includes(ip));
    });
    if (duplicateIp) {
      const usedIp = [duplicateIp.ip_local, duplicateIp.ip_publica, ...(duplicateIp.ips_locales || []), ...(duplicateIp.ips_publicas || [])]
        .map(ip => String(ip || "").trim().toLowerCase()).find(ip => ipsToSave.includes(ip));
      toast.error(`La IP ${usedIp} ya está en uso en ${duplicateIp.nombre}`);
      return;
    }
    setSaving(true);
    try {
      const url = editingActivo ? `${API}/admin/activos/${editingActivo.id}` : `${API}/admin/activos`;
      const res = await fetch(url, {
        method: editingActivo ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(dataToSave)
      });
      if (res.ok) {
        const saved = await res.json();
        // Si es un duplicado y el original tenía credenciales, copiarlas al nuevo activo
        if (!editingActivo && duplicatingFromId && saved.id) {
          try {
            const credsRes = await fetch(`${API}/admin/activos/${duplicatingFromId}/credenciales`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (credsRes.ok) {
              const creds = await credsRes.json();
              await Promise.all(creds.map(cred =>
                fetch(`${API}/admin/activos/${saved.id}/credenciales`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                  body: JSON.stringify({
                    activo_id: saved.id,
                    tipo_acceso: cred.tipo_acceso,
                    usuario: cred.usuario,
                    password: cred.password,
                    url_acceso: cred.url_acceso,
                    observaciones: cred.observaciones
                  })
                })
              ));
              if (creds.length > 0) toast.success(`Activo creado + ${creds.length} credencial(es) copiada(s)`);
            }
          } catch (_) {}
        }
        setDuplicatingFromId(null);
        // Guardar credenciales iniciales (solo al crear sin duplicar)
        if (!editingActivo && !duplicatingFromId && credencialesIniciales.length > 0 && saved.id) {
          const credsToSave = credencialesIniciales.filter(c => c.tipo_acceso || c.usuario || c.password);
          if (credsToSave.length > 0) {
            try {
              const resultados = await Promise.all(credsToSave.map(cred =>
                fetch(`${API}/admin/activos/${saved.id}/credenciales`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ ...cred, activo_id: saved.id })
                })
              ));
              const fallidas = resultados.filter(r => !r.ok);
              if (fallidas.length) {
                let det = "";
                try { det = (await fallidas[0].json()).detail || ""; } catch (_) {}
                toast.error(`Activo creado, pero ${fallidas.length} credencial(es) no se guardaron${det ? `: ${det}` : ""}`);
              } else {
                toast.success(`Activo creado + ${credsToSave.length} credencial(es)`);
              }
            } catch (_) {
              toast.success("Activo creado");
            }
          } else {
            toast.success("Activo creado");
          }
        } else {
          toast.success(editingActivo ? "Activo actualizado" : "Activo creado");
        }
        setCredencialesIniciales([]);
        setShowForm(false); fetchAll();
      } else { const err = await res.json(); toast.error(err.detail || "Error"); }
    } catch { toast.error("Error de conexion"); }
    finally { setSaving(false); }
  };

  const deleteActivo = async (id) => {
    if (!window.confirm("Eliminar este activo?")) return;
    try {
      const res = await fetch(`${API}/admin/activos/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success("Activo eliminado"); fetchAll(); }
    } catch { toast.error("Error"); }
  };

  // Credenciales
  const openCredenciales = async (activo) => {
    setShowCredenciales(activo);
    setShowPasswords({});
    try {
      const res = await fetch(`${API}/admin/activos/${activo.id}/credenciales`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setCredenciales(await res.json());
    } catch { toast.error("Error"); }
  };

  const saveCredencial = async () => {
    if (!credForm) return;
    setSaving(true);
    try {
      const isEdit = credForm.id;
      const url = isEdit ? `${API}/admin/credenciales/${credForm.id}` : `${API}/admin/activos/${showCredenciales.id}/credenciales`;
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ activo_id: showCredenciales.id, ...credForm })
      });
      if (res.ok) { toast.success(isEdit ? "Actualizada" : "Agregada"); setCredForm(null); openCredenciales(showCredenciales); }
    } catch { toast.error("Error"); }
    finally { setSaving(false); }
  };

  const deleteCredencial = async (id) => {
    if (!window.confirm("Eliminar esta credencial?")) return;
    try { await fetch(`${API}/admin/credenciales/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }); toast.success("Eliminada"); openCredenciales(showCredenciales); } catch { toast.error("Error"); }
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success("Copiado"); };

  const loadDetailCredenciales = async (activoId) => {
    setDetailCredLoading(true);
    setDetailCredenciales([]);
    try {
      const res = await fetch(`${API}/admin/activos/${activoId}/credenciales`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setDetailCredenciales(await res.json());
    } catch {
      setDetailCredenciales([]);
    } finally {
      setDetailCredLoading(false);
    }
  };

  const openDetail = (activo) => {
    setShowDetail(activo);
    setDetailPasswords({});
    loadDetailCredenciales(activo.id);
  };

  const navigateToActivo = (activoId) => {
    const target = activos.find(a => a.id === activoId);
    if (target) {
      setShowDetail(target);
      setDetailPasswords({});
      loadDetailCredenciales(target.id);
    }
  };

  // Historial
  const openHistorial = async (activo) => {
    setShowHistorial(activo);
    try {
      const res = await fetch(`${API}/admin/activos/${activo.id}/historial`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setHistorial(await res.json());
    } catch { toast.error("Error"); }
  };

  // Categorias
  const saveCat = async () => {
    if (!catForm.nombre) return;
    setSaving(true);
    try {
      const payload = { nombre: catForm.nombre, subtipos: catForm.subtipos ? catForm.subtipos.split(",").map(s => s.trim()).filter(Boolean) : [], logo_tipo: activeEmpresaPropia?.slug || null, predeterminada: !!catForm.predeterminada && isAdmin && !editingCat };
      const url = editingCat ? `${API}/admin/categorias/${editingCat.id}` : `${API}/admin/categorias`;
      const resSave = await fetch(url, { method: editingCat ? "PUT" : "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) });
      if (!resSave.ok) { const er = await resSave.json().catch(() => ({})); toast.error(er.detail || "Error"); return; }
      toast.success(editingCat ? "Actualizada" : (payload.predeterminada ? "Creada como predeterminada del sistema" : "Creada"));
      setCatForm({ nombre: "", subtipos: "", predeterminada: false }); setEditingCat(null);
      const catRes = await fetch(`${API}/admin/categorias${catQs}`, { headers: { Authorization: `Bearer ${token}` } });
      if (catRes.ok) setCategorias(await catRes.json());
    } catch { toast.error("Error"); }
    finally { setSaving(false); }
  };

  const handleSaveInvConfig = async (permitir) => {
    if (!canGestionarUnicidad) {
      toast.error("No tenés permiso para cambiar la regla de nombres duplicados");
      return;
    }
    setSavingInvConfig(true);
    try {
      const payload = { permitir_nombres_duplicados: !!permitir, logo_tipo: activeEmpresaPropia?.slug || "" };
      const res = await fetch(`${API}/admin/config-inventario`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setInvConfig(await res.json());
        toast.success("Regla de nombres actualizada");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Error al guardar configuración");
      }
    } catch { toast.error("Error al guardar configuración"); }
    finally { setSavingInvConfig(false); }
  };

  const addSubtipoToCat = async (cat) => {
    if (!newSubtipo.trim()) return;
    const updated = { nombre: cat.nombre, subtipos: [...(cat.subtipos || []), newSubtipo.trim()], logo_tipo: cat.logo_tipo ?? "" };
    try {
      await fetch(`${API}/admin/categorias/${cat.id}`, { method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify(updated) });
      toast.success("Subtipo agregado"); setNewSubtipo("");
      const catRes = await fetch(`${API}/admin/categorias${catQs}`, { headers: { Authorization: `Bearer ${token}` } });
      if (catRes.ok) setCategorias(await catRes.json());
    } catch { toast.error("Error"); }
  };

  const removeSubtipo = async (cat, subIdx) => {
    const updated = { nombre: cat.nombre, subtipos: cat.subtipos.filter((_, i) => i !== subIdx), logo_tipo: cat.logo_tipo ?? "" };
    try {
      await fetch(`${API}/admin/categorias/${cat.id}`, { method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify(updated) });
      const catRes = await fetch(`${API}/admin/categorias${catQs}`, { headers: { Authorization: `Bearer ${token}` } });
      if (catRes.ok) setCategorias(await catRes.json());
    } catch { toast.error("Error"); }
  };

  const refreshCats = async () => {
    const r = await fetch(`${API}/admin/categorias${catQs}`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) setCategorias(await r.json());
    if (isAdmin) {
      const e = await fetch(`${API}/admin/categorias/eliminadas${catQs}`, { headers: { Authorization: `Bearer ${token}` } });
      if (e.ok) setCategoriasEliminadas(await e.json());
    }
  };
  const loadPlantillas = async () => {
    const r = await fetch(`${API}/admin/categorias/predeterminadas`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) setPlantillasCat(await r.json());
  };
  const deleteCat = async (id) => {
    if (!window.confirm("¿Eliminar esta categoría SOLO de esta empresa? (se puede recuperar)")) return;
    try {
      const r = await fetch(`${API}/admin/categorias/${id}${catQs}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { const er = await r.json().catch(() => ({})); toast.error(er.detail || "Error"); return; }
      toast.success("Eliminada de esta empresa"); await refreshCats();
    } catch { toast.error("Error"); }
  };
  const restaurarCat = async (id) => {
    const r = await fetch(`${API}/admin/categorias/${id}/restaurar${catQs}`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) { toast.success("Categoría recuperada"); await refreshCats(); } else toast.error("No se pudo recuperar");
  };
  const recuperarTodo = async () => {
    if (!window.confirm("Se repondrán categorías y subtipos predeterminados faltantes y los que ya usan tus activos, solo en esta empresa. ¿Continuar?")) return;
    const r = await fetch(`${API}/admin/categorias/recuperar${catQs}`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) {
      const d = await r.json();
      toast.success(`Recuperado: ${d.restauradas} restauradas, ${d.categorias_agregadas} categorías y ${d.subtipos_agregados} subtipos repuestos`);
      await refreshCats();
    } else toast.error("No se pudo recuperar");
  };
  const deletePlantilla = async (id) => {
    if (!window.confirm("¿Quitar esta categoría de los predeterminados? Las empresas que ya la tienen la conservan.")) return;
    const r = await fetch(`${API}/admin/categorias/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) { toast.success("Quitada de los predeterminados"); loadPlantillas(); }
  };

  const selectedCatSubtipos = useMemo(() => categorias.find(c => c.nombre === formData.categoria)?.subtipos || [], [formData.categoria, categorias]);

  const allIps = (a) => {
    const locals = [a.ip_local, ...(a.ips_locales || [])].filter(Boolean);
    const publics = [a.ip_publica, ...(a.ips_publicas || [])].filter(Boolean);
    return { locals: [...new Set(locals)], publics: [...new Set(publics)] };
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("es-PY", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

  // Get devices that a cuenta is assigned to
  const getAssignedDeviceNames = (activo) => {
    if (!activo.activos_asignados || activo.activos_asignados.length === 0) return [];
    return activo.activos_asignados.map(id => {
      const dev = activos.find(a => a.id === id);
      return dev ? dev.nombre : null;
    }).filter(Boolean);
  };

  // Get cuentas assigned to a device
  const getCuentasForDevice = (deviceId) => {
    return activos.filter(a => isCuenta(a.categoria) && (a.activos_asignados || []).includes(deviceId));
  };

  // Devices for assignment (servidores + dispositivos from same empresa)
  const devicesForAssignment = useMemo(() => {
    if (!formData.empresa_id) return [];
    return activos.filter(a => a.empresa_id === formData.empresa_id && !isCuenta(a.categoria));
  }, [activos, formData.empresa_id]);

  // ===== ACTIVO CARD RENDERER (reused in normal and NVR grouped views) =====
  const renderActivoCard = (activo, idx) => {
    const ips = allIps(activo);
    const assignedDevices = getAssignedDeviceNames(activo);
    const assignedCuentas = !isCuenta(activo.categoria) ? getCuentasForDevice(activo.id) : [];
    const cp = activo.campos_personalizados || {};
    return (
      <motion.div key={activo.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.02 }}
        className="bg-arandu-dark-light border border-white/5 rounded-xl p-3 sm:p-4 hover:border-cyan-500/30 transition-all group" data-testid={`activo-card-${idx}`}>
        <div className="flex items-start justify-between mb-2 sm:mb-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 cursor-pointer" onClick={() => openDetail(activo)} data-testid={`detail-btn-${idx}`}>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center text-cyan-400 flex-shrink-0 group-hover:bg-cyan-500/20 transition-colors">
              {categoriaIconMap[activo.categoria] || <Server className="w-4 h-4 sm:w-5 sm:h-5" />}
            </div>
            <div className="min-w-0">
              <h3 className="text-white font-semibold text-xs sm:text-sm truncate group-hover:text-cyan-400 transition-colors">{activo.nombre}</h3>
              <p className="text-slate-500 text-xs truncate">{activo.subtipo || activo.categoria}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {activo.estado_bien === "bajado" && (
              <span className="text-xs px-2 py-0.5 rounded-full border bg-red-500/20 text-red-400 border-red-500/30">Bajado</span>
            )}
            <span className={`text-xs px-2 py-0.5 rounded-full border ${estadoColors[activo.estado] || estadoColors.activo}`}>{activo.estado}</span>
          </div>
        </div>

        <div className="space-y-1 text-xs mb-2 sm:mb-3">
          {!isCuenta(activo.categoria) && (
            <>
              {ips.locals.length > 0 && <div className="flex justify-between gap-2"><span className="text-slate-500 flex-shrink-0">IP Local</span><span className="text-slate-300 truncate text-right">{ips.locals.join(", ")}</span></div>}
              {ips.publics.length > 0 && <div className="flex justify-between gap-2"><span className="text-slate-500 flex-shrink-0">IP Publica</span><span className="text-slate-300 truncate text-right">{ips.publics.join(", ")}</span></div>}
              {activo.dominio && <div className="flex justify-between gap-2"><span className="text-slate-500 flex-shrink-0">Dominio</span><span className="text-cyan-400 truncate text-right">{activo.dominio}</span></div>}
              {activo.ubicacion && <div className="flex justify-between gap-2"><span className="text-slate-500 flex-shrink-0">Ubicacion</span><span className="text-slate-300 truncate text-right">{activo.ubicacion}</span></div>}
              {activo.nvr_dvr_nombre && <div className="flex justify-between gap-2"><span className="text-slate-500 flex-shrink-0">NVR/DVR</span><span className="text-cyan-300 truncate text-right">{activo.nvr_dvr_nombre}</span></div>}
            </>
          )}
          {isCuenta(activo.categoria) && Object.entries(cp).length > 0 && (
            Object.entries(cp).slice(0, 3).map(([k, v]) => {
              if (!v || k.includes("contrasena") || k.includes("secreto") || k.includes("password")) return null;
              const label = k.replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase());
              return <div key={k} className="flex justify-between gap-2"><span className="text-slate-500 flex-shrink-0">{label}</span><span className="text-slate-300 truncate text-right">{v}</span></div>;
            })
          )}
          {activo.empresa_nombre && !filterEmpresa && <div className="flex justify-between gap-2"><span className="text-slate-500 flex-shrink-0">Empresa</span><span className="text-arandu-blue truncate text-right">{activo.empresa_nombre}</span></div>}
        </div>

        {assignedDevices.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            <Link2 className="w-3 h-3 text-purple-400 mt-0.5" />
            {(activo.activos_asignados || []).map((devId) => {
              const dev = activos.find(a => a.id === devId);
              return dev ? (
                <button key={devId} onClick={() => openDetail(dev)}
                  className="text-xs bg-purple-500/10 text-purple-300 px-1.5 py-0.5 rounded border border-purple-500/20 hover:bg-purple-500/20 hover:border-purple-400/40 transition-all cursor-pointer">
                  {dev.nombre}
                </button>
              ) : null;
            })}
          </div>
        )}

        {assignedCuentas.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            <Key className="w-3 h-3 text-amber-400 mt-0.5" />
            {assignedCuentas.map((c) => (
              <button key={c.id} onClick={() => openDetail(c)}
                className="text-xs bg-amber-500/10 text-amber-300 px-1.5 py-0.5 rounded border border-amber-500/20 hover:bg-amber-500/20 hover:border-amber-400/40 transition-all cursor-pointer">
                {c.subtipo || c.nombre}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-1 pt-2 border-t border-white/5 flex-wrap">
          {!isCuenta(activo.categoria) && (
            <button onClick={() => openCredenciales(activo)} className="flex items-center gap-1 px-2 py-1 rounded text-xs text-amber-400 hover:bg-amber-500/10" data-testid={`cred-btn-${idx}`}><Key className="w-3 h-3" /> {activo.credenciales_count || 0}</button>
          )}
          <button onClick={() => openHistorial(activo)} className="flex items-center gap-1 px-2 py-1 rounded text-xs text-purple-400 hover:bg-purple-500/10" data-testid={`hist-btn-${idx}`}><History className="w-3 h-3" /></button>
          <div className="flex-1" />
          {canEdit && <button onClick={() => duplicateActivo(activo)} title="Duplicar activo" className="p-1.5 rounded text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10" data-testid={`dup-activo-${idx}`}><Copy className="w-3.5 h-3.5" /></button>}
          {canEdit && <button onClick={() => openEditForm(activo)} className="p-1.5 rounded text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10" data-testid={`edit-activo-${idx}`}><Edit className="w-3.5 h-3.5" /></button>}
          {canDelete && <button onClick={() => deleteActivo(activo.id)} className="p-1.5 rounded text-slate-400 hover:text-red-400 hover:bg-red-500/10" data-testid={`del-activo-${idx}`}><Trash2 className="w-3.5 h-3.5" /></button>}
          <AuditoriaRegistroBtn registro={activo} />
        </div>
      </motion.div>
    );
  };

  const renderActivoRow = (activo, idx, options = {}) => {
    const ips = allIps(activo);
    const assignedCuentas = !isCuenta(activo.categoria) ? getCuentasForDevice(activo.id) : [];
    const assignedDevices = getAssignedDeviceNames(activo);
    const cp = activo.campos_personalizados || {};
    const primaryLocalIp = ips.locals[0] || "-";
    const primaryPublicIp = ips.publics[0] || "-";
    const cuentaDato = Object.entries(cp).find(([k, v]) =>
      v && !k.includes("contrasena") && !k.includes("secreto") && !k.includes("password")
    );
    const detalle = isCuenta(activo.categoria)
      ? (cuentaDato ? `${cuentaDato[0].replace(/_/g, " ")}: ${cuentaDato[1]}` : "Cuenta de acceso")
      : (activo.dominio || activo.ubicacion || activo.descripcion || "-");

    const stop = (fn) => (e) => {
      e.stopPropagation();
      fn();
    };

    return (
      <tr
        key={activo.id}
        onClick={() => openDetail(activo)}
        className="border-t border-white/5 hover:bg-cyan-500/5 cursor-pointer transition-colors"
        data-testid={`activo-row-${idx}`}
      >
        <td className="px-3 py-3 min-w-[260px]">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${options.indent ? "bg-cyan-500/5 text-cyan-300" : "bg-cyan-500/10 text-cyan-400"}`}>
              {categoriaIconMap[activo.categoria] || <Server className="w-4 h-4" />}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                {options.indent && <span className="text-cyan-500 text-xs">-</span>}
                <p className="text-white font-semibold text-sm truncate">{activo.nombre}</p>
              </div>
              <p className="text-slate-500 text-xs truncate">{activo.subtipo || activo.categoria || "-"}</p>
            </div>
          </div>
        </td>
        <td className="px-3 py-3 text-slate-300 text-sm min-w-[150px]">
          <span className="font-mono">{primaryLocalIp}</span>
          {ips.locals.length > 1 && <span className="ml-2 text-xs text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded-full">+{ips.locals.length - 1}</span>}
        </td>
        <td className="px-3 py-3 text-slate-400 text-sm min-w-[150px]">
          <span className="font-mono">{primaryPublicIp}</span>
          {ips.publics.length > 1 && <span className="ml-2 text-xs text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded-full">+{ips.publics.length - 1}</span>}
        </td>
        <td className="px-3 py-3 text-slate-300 text-sm min-w-[180px]">
          <p className="truncate max-w-[240px]">{detalle}</p>
          {activo.nvr_dvr_nombre && <p className="text-cyan-400 text-xs truncate">NVR/DVR: {activo.nvr_dvr_nombre}</p>}
        </td>
        <td className="px-3 py-3 text-sm min-w-[140px]">
          <span className="text-arandu-blue truncate block">{activo.empresa_nombre || "-"}</span>
        </td>
        <td className="px-3 py-3 min-w-[120px]">
          <span className={`text-xs px-2 py-0.5 rounded-full border ${estadoColors[activo.estado] || estadoColors.activo}`}>{activo.estado || "activo"}</span>
        </td>
        <td className="px-3 py-3 text-xs text-slate-400 min-w-[130px]">
          <div className="flex items-center gap-2">
            {!isCuenta(activo.categoria) && (
              <span className="inline-flex items-center gap-1 text-amber-400"><Key className="w-3 h-3" /> {activo.credenciales_count || assignedCuentas.length || 0}</span>
            )}
            {isCuenta(activo.categoria) && assignedDevices.length > 0 && (
              <span className="inline-flex items-center gap-1 text-purple-400"><Link2 className="w-3 h-3" /> {assignedDevices.length}</span>
            )}
            <span className="inline-flex items-center gap-1 text-purple-400"><History className="w-3 h-3" /></span>
          </div>
        </td>
        <td className="px-3 py-3 min-w-[150px]">
          <div className="flex items-center justify-end gap-1">
            {!isCuenta(activo.categoria) && (
              <button onClick={stop(() => openCredenciales(activo))} className="p-1.5 rounded text-slate-400 hover:text-amber-400 hover:bg-amber-500/10" title="Credenciales">
                <Key className="w-3.5 h-3.5" />
              </button>
            )}
            <button onClick={stop(() => openHistorial(activo))} className="p-1.5 rounded text-slate-400 hover:text-purple-400 hover:bg-purple-500/10" title="Historial">
              <History className="w-3.5 h-3.5" />
            </button>
            {canEdit && <button onClick={stop(() => duplicateActivo(activo))} title="Duplicar activo" className="p-1.5 rounded text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10"><Copy className="w-3.5 h-3.5" /></button>}
            {canEdit && <button onClick={stop(() => openEditForm(activo))} className="p-1.5 rounded text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10" title="Editar"><Edit className="w-3.5 h-3.5" /></button>}
            {canDelete && <button onClick={stop(() => deleteActivo(activo.id))} className="p-1.5 rounded text-slate-400 hover:text-red-400 hover:bg-red-500/10" title="Eliminar"><Trash2 className="w-3.5 h-3.5" /></button>}
            <AuditoriaRegistroBtn registro={activo} />
          </div>
        </td>
      </tr>
    );
  };

  const SortTh = ({ field, label, className = "" }) => {
    const active = sortBy === field;
    return (
      <th
        onClick={() => toggleSort(field)}
        className={`px-3 py-2 font-semibold cursor-pointer select-none transition-colors hover:text-cyan-400 ${active ? "text-cyan-400" : ""} ${className}`}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          {active
            ? (sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)
            : <ArrowUpDown className="w-3 h-3 opacity-30" />}
        </span>
      </th>
    );
  };

  const ActivosTable = ({ items, catName, title, compact = false, indentRows = false }) => (
    <div className={compact ? "overflow-x-auto" : "bg-arandu-dark-light border border-white/5 rounded-xl overflow-hidden"}>
      {title && (
        <div className="flex items-center gap-2 px-3 py-3 border-b border-white/5">
          <div className="text-cyan-400">{categoriaIconMap[catName] || <Server className="w-5 h-5" />}</div>
          <h2 className="font-heading font-bold text-white text-base">{title}</h2>
          <span className="text-slate-500 text-sm">({items.length})</span>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-arandu-dark/70 text-slate-400 text-xs uppercase">
            <tr>
              {SortTh({ field: "nombre", label: "Activo" })}
              {SortTh({ field: "ip", label: "IP local" })}
              <th className="px-3 py-2 font-semibold">IP publica</th>
              <th className="px-3 py-2 font-semibold">Detalle</th>
              {SortTh({ field: "empresa", label: "Cliente" })}
              <th className="px-3 py-2 font-semibold">Estado</th>
              <th className="px-3 py-2 font-semibold">Refs.</th>
              <th className="px-3 py-2 font-semibold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((activo, idx) => renderActivoRow(activo, idx, { indent: indentRows }))}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="text-arandu-blue animate-pulse text-xl">Cargando inventario...</div></div>;

  const empresaName = filterEmpresa ? empresas.find(e => e.id === filterEmpresa)?.nombre : null;

  return (
    <div className="p-3 sm:p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-6">
        <div>
          <Link to={filterEmpresa ? "/sistema/empresas" : "/sistema"} className="text-slate-400 hover:text-arandu-blue flex items-center gap-2 mb-2 text-sm">
            <ArrowLeft className="w-4 h-4" /> {filterEmpresa ? "Volver a Empresas" : "Volver al Dashboard"}
          </Link>
          <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-white flex items-center gap-3" data-testid="inventario-title">
            <Server className="w-6 h-6 sm:w-8 sm:h-8 text-cyan-400" />
            Inventario Tecnico
          </h1>
          {empresaName && <span className="text-arandu-blue text-sm mt-1 block">{empresaName}</span>}
          <p className="text-slate-400 text-xs mt-1">{filteredActivos.length} activos</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <Button onClick={() => { setShowCategorias(true); refreshCats(); }} className="bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm" data-testid="manage-categories-btn">
              <Settings className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Categorias</span><span className="sm:hidden">Cat.</span>
            </Button>
          )}
          {canCreate && (
            <Button onClick={openNewForm} className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs sm:text-sm" data-testid="new-activo-btn">
              <Plus className="w-4 h-4 mr-1 sm:mr-2" /> Nuevo
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      {canVerConfigTab && (
        <div className="flex gap-1 mb-6 border-b border-white/10">
          {[
            { key: "activos", label: "Activos", icon: <Server className="w-4 h-4" /> },
            { key: "configuracion", label: "Configuración", icon: <Settings className="w-4 h-4" /> },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                activeTab === t.key
                  ? "border-cyan-500 text-cyan-300"
                  : "border-transparent text-slate-400 hover:text-white"
              }`}
              data-testid={`inventario-tab-${t.key}`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      )}

      {activeTab === "activos" && (
      <>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 mb-4">
        <ChipSearch
          chips={searchChips}
          setChips={setSearchChips}
          inputVal={searchInput}
          setInputVal={setSearchInput}
          placeholder="Buscar por cliente, IP, equipo, dominio, usuario, ubicación... Enter para agregar"
        />
	      </div>

{/* Vista toggle */}
<div className="flex gap-2 mb-6 items-center">
  <span className="text-slate-500 text-xs self-center mr-1">Vista:</span>
  {[
    { key: "lista", label: "Lista" },
    { key: "ip_agrupado", label: "IP agrupado" },
  ].map(v => (
    <button
      key={v.key}
      onClick={() => setViewMode(v.key)}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all border ${
        viewMode === v.key
          ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/30"
          : "bg-arandu-dark-lighter text-slate-500 border-white/5 hover:border-white/20"
      }`}
      data-testid={`view-${v.key}`}
    >
      {v.label}
    </button>
  ))}
</div>

      {/* Activos list */}
      {filteredActivos.length === 0 ? (
        <div className="text-center py-16 bg-arandu-dark-light rounded-xl border border-white/5">
          <Server className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          {searchChips.length === 0 && !debouncedInput.trim() ? (
            <p className="text-slate-400 text-lg">Buscá o filtrá por una empresa (cliente) para ver sus activos</p>
          ) : (
            <p className="text-slate-400 text-lg">No hay activos registrados</p>
          )}
          {canCreate && <Button onClick={openNewForm} className="mt-4 bg-cyan-600 hover:bg-cyan-700 text-white"><Plus className="w-4 h-4 mr-2" /> Nuevo Activo</Button>}
        </div>
      ) : viewMode === "ip_agrupado" ? (
        /* ===== NVR/DVR GROUPED VIEW ===== */
        <div>
          {nvrGroups.groups.map(({ nvr, cameras }) => {
            const isExpanded = expandedNvrGroups.has(nvr.id);
            const nvrIps = allIps(nvr);
            return (
              <div key={nvr.id} className="mb-5">
                {/* Group header */}
                <button
                  className="w-full flex items-center gap-3 mb-2 px-3 py-2.5 bg-arandu-dark-light/60 border border-cyan-500/20 rounded-xl hover:border-cyan-500/40 transition-all text-left"
                  onClick={() => setExpandedNvrGroups(prev => {
                    const next = new Set(prev);
                    next.has(nvr.id) ? next.delete(nvr.id) : next.add(nvr.id);
                    return next;
                  })}
                >
                  <div className="w-8 h-8 rounded-lg bg-cyan-500/15 flex items-center justify-center text-cyan-400 flex-shrink-0">
                    <Monitor className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-semibold text-sm">{nvr.nombre}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full border ${estadoColors[nvr.estado] || estadoColors.activo}`}>{nvr.estado}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                      <span>{nvr.subtipo}</span>
                      {nvrIps.locals.length > 0 && <span className="font-mono">{nvrIps.locals[0]}</span>}
                      {nvr.empresa_nombre && !filterEmpresa && <span className="text-arandu-blue">{nvr.empresa_nombre}</span>}
                    </div>
                  </div>
                  <span className="text-cyan-400 text-xs bg-cyan-500/10 px-2 py-1 rounded-full flex-shrink-0 mr-1">
                    {cameras.length} cám.
                  </span>
                  {isExpanded
                    ? <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  }
                </button>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="mb-3">
                        {ActivosTable({ items: [nvr], compact: true })}
                      </div>
                      {cameras.length > 0 && (
                        <div className="ml-4 border-l-2 border-cyan-500/20 pl-4 mb-2">
                          <p className="text-slate-500 text-xs mb-2 flex items-center gap-1.5">
                            <Monitor className="w-3 h-3" /> {cameras.length} camara(s) asociada(s)
                          </p>
                          {ActivosTable({ items: cameras, compact: true, indentRows: true })}
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}

          {/* Standalone items — grouped by categoria */}
          {nvrGroups.standaloneItems.length > 0 && (() => {
            const standaloneGroups = {};
            nvrGroups.standaloneItems.forEach(a => {
              const cat = a.categoria || "Sin Categoria";
              if (!standaloneGroups[cat]) standaloneGroups[cat] = [];
              standaloneGroups[cat].push(a);
            });
            return Object.entries(standaloneGroups).map(([catName, items]) => (
              <div key={catName} className="mb-8" data-testid={`cat-group-${catName}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="text-cyan-400">{categoriaIconMap[catName] || <Server className="w-5 h-5" />}</div>
                  <h2 className="font-heading font-bold text-white text-base sm:text-lg">{catName}</h2>
                  <span className="text-slate-500 text-sm">({items.length})</span>
                </div>
                {ActivosTable({ items, compact: true })}
              </div>
            ));
          })()}
        </div>
      ) : (
        <>
        {ActivosTable({ items: filteredActivos.slice(0, visibleCount) })}
        {filteredActivos.length > visibleCount && (
          <div className="text-center mt-3">
            <Button onClick={() => setVisibleCount(c => c + 200)} variant="ghost" className="text-cyan-400 text-sm">
              Mostrar más ({filteredActivos.length - visibleCount} restantes)
            </Button>
          </div>
        )}
        </>
      )}
      </>
      )}

      {activeTab === "configuracion" && canVerConfigTab && (
        <div className="space-y-4 max-w-2xl">
          <p className="text-slate-400 text-sm">Configuración del módulo Inventario Técnico.</p>

          <div className={`border rounded-xl p-5 flex items-start justify-between gap-4 ${
            invConfig.permitir_nombres_duplicados ? "border-rose-500/25 bg-rose-500/5" : "border-white/10 bg-white/3"
          }`}>
            <div className="flex items-start gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${invConfig.permitir_nombres_duplicados ? "bg-rose-500/15" : "bg-white/5"}`}>
                {invConfig.permitir_nombres_duplicados ? <ToggleRight className="w-4 h-4 text-rose-300" /> : <ToggleLeft className="w-4 h-4 text-slate-500" />}
              </div>
              <div>
                <p className="text-white font-semibold text-sm">Permitir nombres de activo repetidos</p>
                <p className="text-slate-400 text-xs mt-1 max-w-sm">
                  Desactivado por defecto. Si lo activás, se puede registrar más de un activo con el mismo nombre dentro de la misma empresa (cliente).
                </p>
              </div>
            </div>
            <button
              type="button"
              disabled={!canGestionarUnicidad || savingInvConfig}
              onClick={() => handleSaveInvConfig(!invConfig.permitir_nombres_duplicados)}
              className={`shrink-0 px-4 py-2 rounded-lg text-sm font-body transition-colors disabled:opacity-50 ${
                invConfig.permitir_nombres_duplicados ? "bg-rose-500/15 text-rose-200 border border-rose-500/30" : "bg-white/10 text-slate-300 border border-white/10"
              }`}
              title={canGestionarUnicidad ? "Cambiar regla de nombres" : "Sin permiso para cambiar esta regla"}
            >
              {invConfig.permitir_nombres_duplicados ? "Permitidos" : "Bloqueados"}
            </button>
          </div>
        </div>
      )}

      {/* ===== DETAIL MODAL ===== */}
      <AnimatePresence>
        {showDetail && (() => {
          const d = showDetail;
          const ips = allIps(d);
          const cp = d.campos_personalizados || {};
          const detailCuentas = !isCuenta(d.categoria) ? getCuentasForDevice(d.id) : [];
          const detailDevices = isCuenta(d.categoria) ? (d.activos_asignados || []).map(id => activos.find(a => a.id === id)).filter(Boolean) : [];
          const detailCameras = !isCuenta(d.categoria) ? activos.filter(a => a.nvr_dvr_id === d.id) : [];
          const detailParentNvr = d.nvr_dvr_id ? activos.find(a => a.id === d.nvr_dvr_id) : null;
          const camposDef = isCuenta(d.categoria) && d.subtipo ? (CAMPOS_CUENTA[d.subtipo] || []) : [];
          const empresa = empresas.find(e => e.id === d.empresa_id);

          return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
              <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }}
                className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-2xl my-4">
                {/* Header */}
                <div className="sticky top-0 bg-arandu-dark-light/95 backdrop-blur-sm border-b border-white/10 p-4 flex items-center justify-between z-10">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      isCuenta(d.categoria) ? "bg-amber-500/10 text-amber-400" : "bg-cyan-500/10 text-cyan-400"
                    }`}>
                      {categoriaIconMap[d.categoria] || <Server className="w-5 h-5 sm:w-6 sm:h-6" />}
                    </div>
                    <div className="min-w-0">
                      <h2 className="font-heading text-base sm:text-lg font-bold text-white truncate">{d.nombre}</h2>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-slate-400 text-xs">{d.subtipo || d.categoria}</span>
                        {empresa && <span className="text-arandu-blue text-xs">{empresa.nombre}</span>}
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${estadoColors[d.estado] || estadoColors.activo}`}>{d.estado}</span>
                        {d.estado_bien === "bajado" && (
                          <span className="text-xs px-2 py-0.5 rounded-full border bg-red-500/20 text-red-400 border-red-500/30">Bajado</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {canEdit && <button onClick={() => { setShowDetail(null); openEditForm(d); }} className="p-2 text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg"><Edit className="w-4 h-4" /></button>}
                    <button onClick={() => setShowDetail(null)} className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg"><X className="w-4 h-4" /></button>
                  </div>
                </div>

                <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
                  {/* === DEVICE/SERVER DETAIL === */}
                  {!isCuenta(d.categoria) && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {ips.locals.length > 0 && (
                          <div className="bg-arandu-dark rounded-lg p-3 border border-white/5">
                            <p className="text-slate-500 text-xs mb-1">IPs Locales</p>
                            <div className="space-y-1">{ips.locals.map((ip, i) => (
                              <div key={i} className="flex items-center justify-between">
                                <span className="text-white text-sm font-mono">{ip}</span>
                                <button onClick={() => copyToClipboard(ip)} className="text-slate-500 hover:text-cyan-400"><Copy className="w-3 h-3" /></button>
                              </div>
                            ))}</div>
                          </div>
                        )}
                        {ips.publics.length > 0 && (
                          <div className="bg-arandu-dark rounded-lg p-3 border border-white/5">
                            <p className="text-slate-500 text-xs mb-1">IPs Publicas</p>
                            <div className="space-y-1">{ips.publics.map((ip, i) => (
                              <div key={i} className="flex items-center justify-between">
                                <span className="text-white text-sm font-mono">{ip}</span>
                                <button onClick={() => copyToClipboard(ip)} className="text-slate-500 hover:text-cyan-400"><Copy className="w-3 h-3" /></button>
                              </div>
                            ))}</div>
                          </div>
                        )}
                        {d.dominio && (
                          <div className="bg-arandu-dark rounded-lg p-3 border border-white/5">
                            <p className="text-slate-500 text-xs mb-1">Dominio</p>
                            <div className="flex items-center justify-between">
                              <span className="text-cyan-400 text-sm">{d.dominio}</span>
                              <button onClick={() => copyToClipboard(d.dominio)} className="text-slate-500 hover:text-cyan-400"><Copy className="w-3 h-3" /></button>
                            </div>
                          </div>
                        )}
                        {d.ubicacion && (
                          <div className="bg-arandu-dark rounded-lg p-3 border border-white/5">
                            <p className="text-slate-500 text-xs mb-1">Ubicacion</p>
                            <span className="text-white text-sm">{d.ubicacion}</span>
                          </div>
                        )}
                        {(d.puerto_local || d.puerto_externo) && (
                          <div className="bg-arandu-dark rounded-lg p-3 border border-white/5">
                            <p className="text-slate-500 text-xs mb-1">Puertos</p>
                            <span className="text-white text-sm">{d.puerto_local && `Local: ${d.puerto_local}`} {d.puerto_externo && `Ext: ${d.puerto_externo}`}</span>
                          </div>
                        )}
                        {d.version && (
                          <div className="bg-arandu-dark rounded-lg p-3 border border-white/5">
                            <p className="text-slate-500 text-xs mb-1">Version</p>
                            <span className="text-white text-sm">{d.version}</span>
                          </div>
                        )}
                      </div>
                      {/* Datos del bien — adquisición */}
                      {(d.marca || d.modelo || d.chasis || d.numero_factura || d.fecha_adquisicion || d.monto_adquisicion || d.anio_fabricacion) && (
                        <div className="border border-emerald-500/20 rounded-lg overflow-hidden">
                          <div className="bg-emerald-500/5 p-3 border-b border-emerald-500/10">
                            <span className="text-emerald-400 font-semibold text-sm">Datos del bien</span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3">
                            {d.marca && <div><p className="text-slate-500 text-xs mb-0.5">Marca</p><span className="text-white text-sm">{d.marca}</span></div>}
                            {d.modelo && <div><p className="text-slate-500 text-xs mb-0.5">Modelo</p><span className="text-white text-sm">{d.modelo}</span></div>}
                            {d.chasis && <div><p className="text-slate-500 text-xs mb-0.5">Chasis</p><span className="text-white text-sm font-mono">{d.chasis}</span></div>}
                            {d.numero_factura && <div><p className="text-slate-500 text-xs mb-0.5">Nº de Factura</p><span className="text-white text-sm">{d.numero_factura}</span></div>}
                            {d.fecha_adquisicion && <div><p className="text-slate-500 text-xs mb-0.5">Fecha de Adquisición</p><span className="text-white text-sm">{d.fecha_adquisicion}</span></div>}
                            {d.anio_fabricacion != null && <div><p className="text-slate-500 text-xs mb-0.5">Año de Fabricación</p><span className="text-white text-sm">{d.anio_fabricacion}</span></div>}
                            {d.monto_adquisicion != null && <div><p className="text-slate-500 text-xs mb-0.5">Monto de Adquisición</p><span className="text-white text-sm">{fmtMontoAdq(d.monto_adquisicion, d.moneda_adquisicion)}</span></div>}
                          </div>
                        </div>
                      )}
                      {d.descripcion && <div className="bg-arandu-dark rounded-lg p-3 border border-white/5"><p className="text-slate-500 text-xs mb-1">Descripcion</p><p className="text-white text-sm">{d.descripcion}</p></div>}
                      {d.observaciones && <div className="bg-arandu-dark rounded-lg p-3 border border-white/5"><p className="text-slate-500 text-xs mb-1">Observaciones</p><p className="text-slate-300 text-sm">{d.observaciones}</p></div>}

                      {/* CREDENCIALES DIRECTAS DEL DISPOSITIVO */}
                      <div className="border border-amber-500/20 rounded-lg overflow-hidden">
                        <div className="bg-amber-500/5 p-3 flex items-center gap-2 border-b border-amber-500/10">
                          <Key className="w-4 h-4 text-amber-400" />
                          <span className="text-amber-400 font-semibold text-sm">Credenciales directas</span>
                          <span className="text-slate-500 text-xs">({detailCredenciales.length})</span>
                        </div>
                        {detailCredLoading ? (
                          <div className="p-4 text-center"><p className="text-slate-500 text-sm">Cargando credenciales...</p></div>
                        ) : detailCredenciales.length === 0 ? (
                          <div className="p-4 text-center"><p className="text-slate-500 text-sm">Sin credenciales directas cargadas</p></div>
                        ) : (
                          <div className="p-3 space-y-2">
                            {detailCredenciales.map((cred) => {
                              const passKey = `cred_${cred.id}`;
                              const visible = detailPasswords[passKey];
                              return (
                                <div key={cred.id} className="bg-arandu-dark rounded-lg p-3 border border-white/5">
                                  <div className="flex items-start justify-between gap-3 mb-2">
                                    <div className="min-w-0">
                                      <span className="text-amber-400 font-semibold text-xs bg-amber-500/10 px-2 py-0.5 rounded">{cred.tipo_acceso || "Acceso"}</span>
                                      {cred.url_acceso && (
                                        <a href={cred.url_acceso} target="_blank" rel="noreferrer" className="block text-cyan-400 text-xs hover:underline truncate mt-1">
                                          {cred.url_acceso}
                                        </a>
                                      )}
                                    </div>
                                    <button onClick={() => { setShowDetail(null); openCredenciales(d); }} className="text-slate-500 hover:text-amber-400 text-xs flex-shrink-0">Editar</button>
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                      <p className="text-slate-500 text-xs mb-0.5">Usuario / ID</p>
                                      <div className="flex items-center gap-2">
                                        <p className="text-white text-sm font-mono truncate">{cred.usuario || "-"}</p>
                                        {cred.usuario && <button onClick={() => copyToClipboard(cred.usuario)} className="text-slate-500 hover:text-cyan-400 flex-shrink-0"><Copy className="w-3 h-3" /></button>}
                                      </div>
                                    </div>
                                    <div>
                                      <p className="text-slate-500 text-xs mb-0.5">Contrasena</p>
                                      <div className="flex items-center gap-2">
                                        <p className="text-white text-sm font-mono truncate">{visible ? (cred.password || "-") : "********"}</p>
                                        {cred.password && (
                                          <>
                                            <button onClick={() => setDetailPasswords(p => ({...p, [passKey]: !p[passKey]}))} className="text-slate-500 hover:text-amber-400 flex-shrink-0">{visible ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}</button>
                                            <button onClick={() => copyToClipboard(cred.password)} className="text-slate-500 hover:text-cyan-400 flex-shrink-0"><Copy className="w-3 h-3" /></button>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  {cred.observaciones && <p className="text-slate-500 text-xs mt-2 italic">{cred.observaciones}</p>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* ACTIVOS ASOCIADOS */}
                      {(detailCameras.length > 0 || detailParentNvr) && (
                        <div className="border border-cyan-500/20 rounded-lg overflow-hidden">
                          <div className="bg-cyan-500/5 p-3 flex items-center gap-2 border-b border-cyan-500/10">
                            <Link2 className="w-4 h-4 text-cyan-400" />
                            <span className="text-cyan-400 font-semibold text-sm">Activos asociados</span>
                            <span className="text-slate-500 text-xs">({detailCameras.length + (detailParentNvr ? 1 : 0)})</span>
                          </div>
                          <div className="p-3 space-y-2">
                            {detailParentNvr && (
                              <button onClick={() => navigateToActivo(detailParentNvr.id)}
                                className="w-full bg-arandu-dark rounded-lg p-3 border border-white/5 hover:border-cyan-500/30 transition-all text-left flex items-center gap-3">
                                <div className="w-9 h-9 rounded-lg bg-cyan-500/10 flex items-center justify-center text-cyan-400 flex-shrink-0">
                                  {categoriaIconMap[detailParentNvr.categoria] || <Server className="w-4 h-4" />}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-white text-sm font-medium truncate">{detailParentNvr.nombre}</p>
                                  <p className="text-slate-500 text-xs">NVR/DVR asociado</p>
                                </div>
                                <Eye className="w-4 h-4 text-slate-500 flex-shrink-0" />
                              </button>
                            )}
                            {detailCameras.map(cam => {
                              const camIps = allIps(cam);
                              return (
                                <button key={cam.id} onClick={() => navigateToActivo(cam.id)}
                                  className="w-full bg-arandu-dark rounded-lg p-3 border border-white/5 hover:border-cyan-500/30 transition-all text-left flex items-center gap-3">
                                  <div className="w-9 h-9 rounded-lg bg-cyan-500/10 flex items-center justify-center text-cyan-400 flex-shrink-0">
                                    {categoriaIconMap[cam.categoria] || <Server className="w-4 h-4" />}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-white text-sm font-medium truncate">{cam.nombre}</p>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-slate-500 text-xs">{cam.subtipo || cam.categoria}</span>
                                      {camIps.locals.length > 0 && <span className="text-slate-400 text-xs font-mono">{camIps.locals[0]}</span>}
                                    </div>
                                  </div>
                                  <Eye className="w-4 h-4 text-slate-500 flex-shrink-0" />
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* CUENTAS DE ACCESO ASIGNADAS COMO ACTIVOS */}
                      <div className="border border-amber-500/20 rounded-lg overflow-hidden">
                        <div className="bg-amber-500/5 p-3 flex items-center gap-2 border-b border-amber-500/10">
                          <Key className="w-4 h-4 text-amber-400" />
                          <span className="text-amber-400 font-semibold text-sm">Cuentas de acceso asignadas como activos</span>
                          <span className="text-slate-500 text-xs">({detailCuentas.length})</span>
                        </div>
                        {detailCuentas.length === 0 ? (
                          <div className="p-4 text-center"><p className="text-slate-500 text-sm">Sin cuentas asignadas a este equipo</p></div>
                        ) : (
                          <div className="p-3 space-y-2">
                            {detailCuentas.map(cuenta => {
                              const ccp = cuenta.campos_personalizados || {};
                              const cDef = CAMPOS_CUENTA[cuenta.subtipo] || [];
                              return (
                                <div key={cuenta.id} className="bg-arandu-dark rounded-lg p-3 border border-white/5 hover:border-amber-500/20 transition-all cursor-pointer"
                                  onClick={() => navigateToActivo(cuenta.id)} data-testid={`detail-cuenta-${cuenta.id}`}>
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-amber-400 font-semibold text-xs bg-amber-500/10 px-2 py-0.5 rounded">{cuenta.subtipo}</span>
                                      <span className="text-white text-sm">{cuenta.nombre}</span>
                                    </div>
                                    <Eye className="w-3.5 h-3.5 text-slate-500" />
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {cDef.length > 0 ? cDef.map(campo => {
                                      const val = ccp[campo.key];
                                      if (!val) return null;
                                      const isHidden = campo.isPassword && !detailPasswords[`${cuenta.id}_${campo.key}`];
                                      return (
                                        <div key={campo.key} className="flex items-center justify-between gap-1">
                                          <div className="min-w-0">
                                            <p className="text-slate-500 text-xs">{campo.label}</p>
                                            <p className="text-white text-xs font-mono truncate">{isHidden ? "********" : val}</p>
                                          </div>
                                          <div className="flex gap-0.5 flex-shrink-0">
                                            {campo.isPassword && (
                                              <button onClick={(e) => { e.stopPropagation(); setDetailPasswords(p => ({...p, [`${cuenta.id}_${campo.key}`]: !p[`${cuenta.id}_${campo.key}`]})); }}
                                                className="p-1 text-slate-500 hover:text-amber-400">
                                                {isHidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                              </button>
                                            )}
                                            <button onClick={(e) => { e.stopPropagation(); copyToClipboard(val); }} className="p-1 text-slate-500 hover:text-cyan-400"><Copy className="w-3 h-3" /></button>
                                          </div>
                                        </div>
                                      );
                                    }) : Object.entries(ccp).map(([k, v]) => {
                                      if (!v) return null;
                                      const isPw = k.includes("contrasena") || k.includes("password") || k.includes("secreto");
                                      const isHidden = isPw && !detailPasswords[`${cuenta.id}_${k}`];
                                      return (
                                        <div key={k} className="flex items-center justify-between gap-1">
                                          <div className="min-w-0">
                                            <p className="text-slate-500 text-xs">{k.replace(/_/g, " ")}</p>
                                            <p className="text-white text-xs font-mono truncate">{isHidden ? "********" : v}</p>
                                          </div>
                                          <div className="flex gap-0.5 flex-shrink-0">
                                            {isPw && <button onClick={(e) => { e.stopPropagation(); setDetailPasswords(p => ({...p, [`${cuenta.id}_${k}`]: !p[`${cuenta.id}_${k}`]})); }} className="p-1 text-slate-500 hover:text-amber-400">{isHidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}</button>}
                                            <button onClick={(e) => { e.stopPropagation(); copyToClipboard(v); }} className="p-1 text-slate-500 hover:text-cyan-400"><Copy className="w-3 h-3" /></button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* === CUENTA DE ACCESO DETAIL === */}
                  {isCuenta(d.categoria) && (
                    <>
                      {/* Full credential fields */}
                      <div className="border border-amber-500/20 rounded-lg overflow-hidden">
                        <div className="bg-amber-500/5 p-3 flex items-center gap-2 border-b border-amber-500/10">
                          <Key className="w-4 h-4 text-amber-400" />
                          <span className="text-amber-400 font-semibold text-sm">Datos de {d.subtipo}</span>
                        </div>
                        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {camposDef.length > 0 ? camposDef.map(campo => {
                            const val = cp[campo.key];
                            if (!val) return null;
                            const isHidden = campo.isPassword && !detailPasswords[campo.key];
                            return (
                              <div key={campo.key} className="bg-arandu-dark rounded-lg p-3 border border-white/5">
                                <p className="text-slate-500 text-xs mb-1">{campo.label}</p>
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-white text-sm font-mono truncate">{isHidden ? "********" : val}</span>
                                  <div className="flex gap-1 flex-shrink-0">
                                    {campo.isPassword && (
                                      <button onClick={() => setDetailPasswords(p => ({...p, [campo.key]: !p[campo.key]}))}
                                        className="p-1 text-slate-500 hover:text-amber-400">
                                        {isHidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                                      </button>
                                    )}
                                    <button onClick={() => copyToClipboard(val)} className="p-1 text-slate-500 hover:text-cyan-400"><Copy className="w-3.5 h-3.5" /></button>
                                  </div>
                                </div>
                              </div>
                            );
                          }) : Object.entries(cp).map(([k, v]) => {
                            if (!v) return null;
                            const isPw = k.includes("contrasena") || k.includes("password") || k.includes("secreto");
                            const isHidden = isPw && !detailPasswords[k];
                            return (
                              <div key={k} className="bg-arandu-dark rounded-lg p-3 border border-white/5">
                                <p className="text-slate-500 text-xs mb-1">{k.replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase())}</p>
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-white text-sm font-mono truncate">{isHidden ? "********" : v}</span>
                                  <div className="flex gap-1 flex-shrink-0">
                                    {isPw && <button onClick={() => setDetailPasswords(p => ({...p, [k]: !p[k]}))} className="p-1 text-slate-500 hover:text-amber-400">{isHidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}</button>}
                                    <button onClick={() => copyToClipboard(v)} className="p-1 text-slate-500 hover:text-cyan-400"><Copy className="w-3.5 h-3.5" /></button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Assigned devices - clickable to navigate */}
                      <div className="border border-purple-500/20 rounded-lg overflow-hidden">
                        <div className="bg-purple-500/5 p-3 flex items-center gap-2 border-b border-purple-500/10">
                          <Link2 className="w-4 h-4 text-purple-400" />
                          <span className="text-purple-400 font-semibold text-sm">Dispositivos Asignados</span>
                          <span className="text-slate-500 text-xs">({detailDevices.length})</span>
                        </div>
                        {detailDevices.length === 0 ? (
                          <div className="p-4 text-center"><p className="text-slate-500 text-sm">Sin dispositivos asignados</p></div>
                        ) : (
                          <div className="p-3 space-y-2">
                            {detailDevices.map(dev => {
                              const devIps = allIps(dev);
                              return (
                                <button key={dev.id} onClick={() => navigateToActivo(dev.id)}
                                  className="w-full bg-arandu-dark rounded-lg p-3 border border-white/5 hover:border-purple-500/30 transition-all text-left flex items-center gap-3"
                                  data-testid={`detail-device-${dev.id}`}>
                                  <div className="w-9 h-9 rounded-lg bg-cyan-500/10 flex items-center justify-center text-cyan-400 flex-shrink-0">
                                    {categoriaIconMap[dev.categoria] || <Server className="w-4 h-4" />}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-white text-sm font-medium truncate">{dev.nombre}</p>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-slate-500 text-xs">{dev.subtipo || dev.categoria}</span>
                                      {devIps.locals.length > 0 && <span className="text-slate-400 text-xs font-mono">{devIps.locals[0]}</span>}
                                    </div>
                                  </div>
                                  <Eye className="w-4 h-4 text-slate-500 flex-shrink-0" />
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {d.observaciones && <div className="bg-arandu-dark rounded-lg p-3 border border-white/5"><p className="text-slate-500 text-xs mb-1">Observaciones</p><p className="text-slate-300 text-sm">{d.observaciones}</p></div>}
                    </>
                  )}

                  <p className="text-slate-600 text-xs text-right">Creado: {formatDate(d.created_at)}</p>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* ===== FORM MODAL ===== */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-3xl my-4">
              <div className="sticky top-0 bg-arandu-dark-light/95 backdrop-blur-sm border-b border-white/10 p-3 sm:p-4 flex justify-between items-center z-10">
                <h2 className="font-heading text-base sm:text-lg font-bold text-white">{editingActivo ? "Editar Activo" : "Nuevo Activo"}</h2>
                <div className="flex gap-2">
                  <Button onClick={saveActivo} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs sm:text-sm" data-testid="save-activo-btn"><Save className="w-4 h-4 mr-1" /> {saving ? "..." : "Guardar"}</Button>
                  <Button onClick={() => setShowForm(false)} variant="ghost" className="text-slate-400"><X className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="p-3 sm:p-4 space-y-4 max-h-[75vh] overflow-y-auto">
                {/* Basic: Empresa + Categoria + Subtipo */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <label className="text-slate-400 text-xs mb-1 block">Empresa *</label>
                    <select value={formData.empresa_id} onChange={(e) => setFormData({...formData, empresa_id: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="form-empresa">
                      <option value="">Seleccionar empresa...</option>
                      {(contextEmpresaId ? empresas.filter(e => e.id === contextEmpresaId) : empresas)
                        .map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-slate-400 text-xs mb-1 block">Categoria *</label>
                    <select value={formData.categoria} onChange={(e) => setFormData({...formData, categoria: e.target.value, subtipo: "", campos_personalizados: {}})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="form-categoria">
                      <option value="">Seleccionar...</option>
                      {categorias.map(c => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-slate-400 text-xs mb-1 block">Subtipo</label>
                    <select value={formData.subtipo} onChange={(e) => {
                      const newSubtipo = e.target.value;
                      setFormData(f => ({...f, subtipo: newSubtipo, campos_personalizados: isCuenta(f.categoria) ? {} : f.campos_personalizados }));
                    }} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="form-subtipo">
                      <option value="">Seleccionar...</option>
                      {selectedCatSubtipos.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                {/* Nombre + Descripcion */}
                <div>
                  <label className="text-slate-400 text-xs mb-1 block">Nombre *</label>
                  <Input value={formData.nombre} onChange={(e) => setFormData({...formData, nombre: e.target.value})} className="bg-arandu-dark border-white/10 text-white" placeholder={isCuenta(formData.categoria) ? "Ej: AnyDesk - Recepcion" : "Nombre del equipo..."} data-testid="form-nombre" />
                </div>

                {/* ====== CAMPOS PARA CUENTAS DE ACCESO ====== */}
                {isCuenta(formData.categoria) && formData.subtipo && CAMPOS_CUENTA[formData.subtipo] && (
                  <div className="border border-amber-500/20 rounded-lg p-3 sm:p-4 bg-amber-500/5">
                    <label className="text-amber-400 text-xs font-semibold mb-3 block flex items-center gap-1">
                      <Key className="w-3.5 h-3.5" /> Datos de {formData.subtipo}
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {CAMPOS_CUENTA[formData.subtipo].map(campo => (
                        <div key={campo.key}>
                          <label className="text-slate-400 text-xs mb-1 block">{campo.label}</label>
                          <div className="relative">
                            <Input
                              type={campo.isPassword && !showFieldPasswords[campo.key] ? "password" : "text"}
                              value={formData.campos_personalizados[campo.key] || ""}
                              onChange={(e) => updateCampo(campo.key, e.target.value)}
                              className="bg-arandu-dark border-white/10 text-white pr-8"
                              placeholder={campo.placeholder}
                              data-testid={`campo-${campo.key}`}
                            />
                            {campo.isPassword && (
                              <button type="button" onClick={() => setShowFieldPasswords(p => ({...p, [campo.key]: !p[campo.key]}))}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-amber-400">
                                {showFieldPasswords[campo.key] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ====== ASIGNAR A DISPOSITIVOS (solo para cuentas) ====== */}
                {isCuenta(formData.categoria) && formData.empresa_id && (
                  <div className="border border-purple-500/20 rounded-lg p-3 sm:p-4 bg-purple-500/5">
                    <label className="text-purple-400 text-xs font-semibold mb-2 block flex items-center gap-1">
                      <Link2 className="w-3.5 h-3.5" /> Asignar a Dispositivos/Servidores
                    </label>
                    {devicesForAssignment.length === 0 ? (
                      <p className="text-slate-500 text-xs">No hay dispositivos/servidores en esta empresa</p>
                    ) : (
                      <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                        {devicesForAssignment.map(dev => (
                          <button key={dev.id} type="button" onClick={() => toggleActivoAsignado(dev.id)}
                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-all ${
                              formData.activos_asignados.includes(dev.id)
                                ? "bg-purple-500/20 text-purple-300 border border-purple-500/40"
                                : "bg-arandu-dark text-slate-400 border border-white/5 hover:border-white/20"
                            }`} data-testid={`assign-dev-${dev.id}`}>
                            {formData.activos_asignados.includes(dev.id) && <Check className="w-3 h-3" />}
                            <Monitor className="w-3 h-3" />
                            {dev.nombre}
                            <span className="text-slate-600">({dev.subtipo || dev.categoria})</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ====== CAMPOS PARA DISPOSITIVOS/SERVIDORES/DOMINIOS ====== */}
                {!isCuenta(formData.categoria) && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><label className="text-slate-400 text-xs mb-1 block">Descripcion</label>
                        <textarea value={formData.descripcion} onChange={(e) => setFormData({...formData, descripcion: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm min-h-[50px]" placeholder="Descripcion..." data-testid="form-desc" />
                      </div>
                      <div className="space-y-3">
                        <div><label className="text-slate-400 text-xs mb-1 block">Ubicacion</label><Input value={formData.ubicacion} onChange={(e) => setFormData({...formData, ubicacion: e.target.value})} className="bg-arandu-dark border-white/10 text-white" placeholder="Rack 1..." /></div>
                        <div>
                          <label className="text-slate-400 text-xs mb-1 block">Estado</label>
                          <select value={formData.estado} onChange={(e) => setFormData({...formData, estado: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm"><option value="activo">Activo</option><option value="inactivo">Inactivo</option><option value="mantenimiento">Mantenimiento</option></select>
                        </div>
                        {/* Selector NVR/DVR para Cámaras IP */}
                        {formData.categoria === "Dispositivos" && formData.subtipo === "Camara IP" && (
                          <div>
                            <label className="text-slate-400 text-xs mb-1 block">NVR / DVR asociado</label>
                            <select
                              value={formData.nvr_dvr_id || ""}
                              onChange={(e) => setFormData({...formData, nvr_dvr_id: e.target.value || null})}
                              className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
                            >
                              <option value="">Sin asociar</option>
                              {activos.filter(a => a.empresa_id === formData.empresa_id && a.categoria === "Dispositivos" && (a.subtipo === "NVR" || a.subtipo === "DVR")).map(a => (
                                <option key={a.id} value={a.id}>{a.nombre} ({a.subtipo})</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Multi IP Local */}
                    <div className="border border-white/5 rounded-lg p-3">
                      <label className="text-cyan-400 text-xs font-semibold mb-2 block">IPs Locales</label>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {formData.ips_locales.map((ip, i) => (
                          editingIp?.type === "local" && editingIp?.index === i ? (
                            <span key={i} className="flex items-center gap-1">
                              <Input
                                autoFocus
                                value={editingIp.value}
                                onChange={(e) => setEditingIp(prev => ({ ...prev, value: e.target.value }))}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") { e.preventDefault(); confirmEditIp(); }
                                  if (e.key === "Escape") { e.preventDefault(); setEditingIp(null); }
                                }}
                                onBlur={confirmEditIp}
                                className="bg-arandu-dark border-cyan-500/50 text-white text-xs h-7 w-36 px-2"
                              />
                            </span>
                          ) : (
                            <span key={i} className="flex items-center gap-1 bg-arandu-dark text-white text-xs px-2 py-1 rounded border border-white/10 cursor-pointer hover:border-cyan-500/50 hover:bg-cyan-900/20" onClick={() => startEditIp("local", i)} title="Click para editar">
                              {ip} <button onClick={(e) => { e.stopPropagation(); removeIp("local", i); }} className="text-red-400 hover:text-red-300"><X className="w-3 h-3" /></button>
                            </span>
                          )
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input value={newIpLocal} onChange={(e) => setNewIpLocal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addIp("local"))} className="bg-arandu-dark border-white/10 text-white flex-1" placeholder="192.168.1.x" data-testid="form-ip-local" />
                        <Button type="button" onClick={() => addIp("local")} className="bg-cyan-600/50 hover:bg-cyan-600 text-white text-xs"><Plus className="w-3 h-3" /></Button>
                      </div>
                    </div>

                    {/* Multi IP Publica */}
                    <div className="border border-white/5 rounded-lg p-3">
                      <label className="text-cyan-400 text-xs font-semibold mb-2 block">IPs Publicas</label>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {formData.ips_publicas.map((ip, i) => (
                          editingIp?.type === "publica" && editingIp?.index === i ? (
                            <span key={i} className="flex items-center gap-1">
                              <Input
                                autoFocus
                                value={editingIp.value}
                                onChange={(e) => setEditingIp(prev => ({ ...prev, value: e.target.value }))}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") { e.preventDefault(); confirmEditIp(); }
                                  if (e.key === "Escape") { e.preventDefault(); setEditingIp(null); }
                                }}
                                onBlur={confirmEditIp}
                                className="bg-arandu-dark border-cyan-500/50 text-white text-xs h-7 w-36 px-2"
                              />
                            </span>
                          ) : (
                            <span key={i} className="flex items-center gap-1 bg-arandu-dark text-white text-xs px-2 py-1 rounded border border-white/10 cursor-pointer hover:border-cyan-500/50 hover:bg-cyan-900/20" onClick={() => startEditIp("publica", i)} title="Click para editar">
                              {ip} <button onClick={(e) => { e.stopPropagation(); removeIp("publica", i); }} className="text-red-400 hover:text-red-300"><X className="w-3 h-3" /></button>
                            </span>
                          )
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input value={newIpPublica} onChange={(e) => setNewIpPublica(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addIp("publica"))} className="bg-arandu-dark border-white/10 text-white flex-1" placeholder="200.x.x.x" data-testid="form-ip-publica" />
                        <Button type="button" onClick={() => addIp("publica")} className="bg-cyan-600/50 hover:bg-cyan-600 text-white text-xs"><Plus className="w-3 h-3" /></Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><label className="text-slate-400 text-xs mb-1 block">Dominio</label><Input value={formData.dominio} onChange={(e) => setFormData({...formData, dominio: e.target.value})} className="bg-arandu-dark border-white/10 text-white" placeholder="ejemplo.com" /></div>
                      <div><label className="text-slate-400 text-xs mb-1 block">Version</label><Input value={formData.version} onChange={(e) => setFormData({...formData, version: e.target.value})} className="bg-arandu-dark border-white/10 text-white" placeholder="v6.49..." /></div>
                      <div><label className="text-slate-400 text-xs mb-1 block">Puerto Local</label><Input value={formData.puerto_local} onChange={(e) => setFormData({...formData, puerto_local: e.target.value})} className="bg-arandu-dark border-white/10 text-white" placeholder="8080" /></div>
                      <div><label className="text-slate-400 text-xs mb-1 block">Puerto Externo</label><Input value={formData.puerto_externo} onChange={(e) => setFormData({...formData, puerto_externo: e.target.value})} className="bg-arandu-dark border-white/10 text-white" placeholder="443" /></div>
                    </div>
                  </>
                )}

                {/* ── Hardware specs — PC Escritorio / Notebook ── */}
                {formData.categoria === "Dispositivos" && (formData.subtipo === "PC Escritorio" || formData.subtipo === "Notebook") && (
                  <div className="border border-blue-500/20 rounded-lg p-3 space-y-3 bg-blue-500/5">
                    <label className="text-blue-400 text-xs font-semibold flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                      Especificaciones del equipo
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Nombre del equipo (hostname)</label>
                        <input value={formData.nombre_equipo} onChange={e => setFormData({...formData, nombre_equipo: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm font-mono" placeholder="PC-RECEPCION-01" />
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Dirección MAC</label>
                        <input value={formData.mac_address} onChange={e => setFormData({...formData, mac_address: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm font-mono" placeholder="AA:BB:CC:DD:EE:FF" />
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Procesador</label>
                        <input value={formData.procesador} onChange={e => setFormData({...formData, procesador: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm" placeholder="Intel Core i5-12400" />
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Memoria RAM</label>
                        <input value={formData.ram} onChange={e => setFormData({...formData, ram: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm" placeholder="8 GB DDR4" />
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Disco duro</label>
                        <input value={formData.disco_duro} onChange={e => setFormData({...formData, disco_duro: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm" placeholder="SSD 512 GB" />
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Monitor (marca/modelo)</label>
                        <input value={formData.marca_monitor} onChange={e => setFormData({...formData, marca_monitor: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm" placeholder={'Samsung 24" FHD'} />
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Usuario actual</label>
                        <input value={formData.usuario_actual} onChange={e => setFormData({...formData, usuario_actual: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm" placeholder="Juan Pérez" />
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Departamento</label>
                        <input value={formData.departamento} onChange={e => setFormData({...formData, departamento: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm" placeholder="Administración" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="text-slate-400 text-xs mb-1 block">Cuenta Office / Microsoft 365</label>
                        <input value={formData.cuenta_office} onChange={e => setFormData({...formData, cuenta_office: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm" placeholder="usuario@empresa.com" />
                      </div>
                    </div>
                  </div>
                )}

                {/* ====== DATOS DEL BIEN (ADQUISICIÓN) ====== */}
                {!isCuenta(formData.categoria) && (
                  <div className="border border-emerald-500/20 rounded-lg p-3 space-y-3 bg-emerald-500/5">
                    <label className="text-emerald-400 text-xs font-semibold flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3v-3m-3 3v-3m9-10H3a2 2 0 00-2 2v10a2 2 0 002 2h18a2 2 0 002-2V9a2 2 0 00-2-2z" /></svg>
                      Datos del bien
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Marca</label>
                        <Input value={formData.marca} onChange={(e) => setFormData({...formData, marca: e.target.value})} className="bg-arandu-dark border-white/10 text-white" placeholder="Dell, HP, Toyota..." />
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Modelo</label>
                        <Input value={formData.modelo} onChange={(e) => setFormData({...formData, modelo: e.target.value})} className="bg-arandu-dark border-white/10 text-white" placeholder="..." />
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Chasis</label>
                        <Input value={formData.chasis} onChange={(e) => setFormData({...formData, chasis: e.target.value})} className="bg-arandu-dark border-white/10 text-white font-mono" placeholder="Nº de chasis / serie" />
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Nº de Factura</label>
                        <Input value={formData.numero_factura} onChange={(e) => setFormData({...formData, numero_factura: e.target.value})} className="bg-arandu-dark border-white/10 text-white" placeholder="001-001-0001234" />
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Fecha de Adquisición</label>
                        <DateInput value={formData.fecha_adquisicion} onChange={(e) => setFormData({...formData, fecha_adquisicion: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Año de Fabricación</label>
                        <input type="number" value={formData.anio_fabricacion} onChange={(e) => setFormData({...formData, anio_fabricacion: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm" placeholder="2023" min="1980" max="2100" />
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Monto de Adquisición</label>
                        <div className="flex gap-2">
                          <select value={formData.moneda_adquisicion || "PYG"} onChange={(e) => setFormData({...formData, moneda_adquisicion: e.target.value})} className="bg-arandu-dark border border-white/10 text-white rounded-lg px-2 py-2 text-sm shrink-0">
                            <option value="PYG">₲ PYG</option>
                            <option value="USD">$ USD</option>
                            <option value="BRL">R$ BRL</option>
                          </select>
                          <input type="text" inputMode={(formData.moneda_adquisicion || "PYG") === "PYG" ? "numeric" : "decimal"} value={formatMoneyInput(formData.monto_adquisicion, formData.moneda_adquisicion || "PYG")} onChange={(e) => setFormData({...formData, monto_adquisicion: normalizeMoneyValue(e.target.value, formData.moneda_adquisicion || "PYG")})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm" placeholder="0" />
                        </div>
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Estado del bien</label>
                        <select value={formData.estado_bien} onChange={(e) => setFormData({...formData, estado_bien: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm">
                          <option value="normal">Normal</option>
                          <option value="bajado">Bajado</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Observaciones + Estado (shared) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {isCuenta(formData.categoria) && (
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">Estado</label>
                      <select value={formData.estado} onChange={(e) => setFormData({...formData, estado: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm"><option value="activo">Activo</option><option value="inactivo">Inactivo</option><option value="mantenimiento">Mantenimiento</option></select>
                    </div>
                  )}
                  <div className={isCuenta(formData.categoria) ? "" : "sm:col-span-2"}>
                    <label className="text-slate-400 text-xs mb-1 block">Observaciones</label>
                    <textarea value={formData.observaciones} onChange={(e) => setFormData({...formData, observaciones: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm min-h-[50px]" placeholder="Notas..." />
                  </div>
                </div>

                {!editingActivo && !duplicatingFromId && !hasPermission("credenciales.editar") && (
                  <p className="mt-3 border-t border-white/10 pt-3 text-xs text-slate-500">
                    <Key className="w-3 h-3 inline mr-1" />
                    Tu usuario no tiene el permiso "Editar credenciales de acceso" (credenciales.editar): pedí a un administrador que lo habilite para cargar credenciales junto con el activo.
                  </p>
                )}
                {/* Credenciales iniciales — solo al crear (no al editar ni duplicar) */}
                {!editingActivo && !duplicatingFromId && hasPermission("credenciales.editar") && (
                  <div className="mt-3 border-t border-white/10 pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-amber-400 text-xs font-semibold flex items-center gap-1">
                        <Key className="w-3 h-3" /> Credenciales <span className="text-slate-500 font-normal">(opcional)</span>
                      </label>
                      <button type="button"
                        onClick={() => setCredencialesIniciales(prev => [...prev, { tipo_acceso: "", usuario: "", password: "", url_acceso: "", observaciones: "" }])}
                        className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1 transition-colors">
                        <Plus className="w-3 h-3" /> Agregar credencial
                      </button>
                    </div>
                    {credencialesIniciales.map((cred, idx) => (
                      <div key={idx} className="bg-arandu-dark border border-amber-500/20 rounded-lg p-3 mb-2">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-amber-400 text-xs font-semibold">Credencial {idx + 1}</span>
                          <button type="button" onClick={() => setCredencialesIniciales(prev => prev.filter((_, i) => i !== idx))}
                            className="text-slate-500 hover:text-red-400 transition-colors"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <label className="text-slate-400 text-xs mb-1 block">Tipo de Acceso</label>
                            <select value={cred.tipo_acceso}
                              onChange={(e) => setCredencialesIniciales(prev => prev.map((c, i) => i === idx ? {...c, tipo_acceso: e.target.value} : c))}
                              className="w-full bg-arandu-dark-light border border-white/10 text-white rounded-lg px-3 py-2 text-sm">
                              <option value="">Seleccionar...</option>
                              <option value="SSH">SSH</option><option value="RDP">RDP</option><option value="Winbox">Winbox</option>
                              <option value="Panel Web">Panel Web</option><option value="FTP">FTP</option><option value="VPN">VPN</option>
                              <option value="WiFi">WiFi</option><option value="Base de Datos">Base de Datos</option>
                              <option value="NID">NID</option><option value="Otro">Otro</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-slate-400 text-xs mb-1 block">Usuario</label>
                            <Input value={cred.usuario}
                              onChange={(e) => setCredencialesIniciales(prev => prev.map((c, i) => i === idx ? {...c, usuario: e.target.value} : c))}
                              className="bg-arandu-dark-light border-white/10 text-white" placeholder="usuario" />
                          </div>
                          <div>
                            <label className="text-slate-400 text-xs mb-1 block">Password</label>
                            <Input type="password" value={cred.password}
                              onChange={(e) => setCredencialesIniciales(prev => prev.map((c, i) => i === idx ? {...c, password: e.target.value} : c))}
                              className="bg-arandu-dark-light border-white/10 text-white" placeholder="contraseña" />
                          </div>
                          <div>
                            <label className="text-slate-400 text-xs mb-1 block">URL / IP</label>
                            <Input value={cred.url_acceso}
                              onChange={(e) => setCredencialesIniciales(prev => prev.map((c, i) => i === idx ? {...c, url_acceso: e.target.value} : c))}
                              className="bg-arandu-dark-light border-white/10 text-white" placeholder="192.168.1.1" />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="text-slate-400 text-xs mb-1 block">Observaciones</label>
                            <Input value={cred.observaciones}
                              onChange={(e) => setCredencialesIniciales(prev => prev.map((c, i) => i === idx ? {...c, observaciones: e.target.value} : c))}
                              className="bg-arandu-dark-light border-white/10 text-white" placeholder="Notas..." />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== CREDENCIALES MODAL ===== */}
      <AnimatePresence>
        {showCredenciales && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-3xl my-4">
              <div className="sticky top-0 bg-arandu-dark-light/95 backdrop-blur-sm border-b border-white/10 p-3 sm:p-4 flex justify-between items-center z-10">
                <div className="min-w-0">
                  <h2 className="font-heading text-base sm:text-lg font-bold text-white flex items-center gap-2"><Key className="w-5 h-5 text-amber-400" /> Credenciales</h2>
                  <p className="text-slate-400 text-xs sm:text-sm truncate">{showCredenciales.nombre}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button onClick={() => setCredForm({ tipo_acceso: "", usuario: "", password: "", url_acceso: "", observaciones: "" })} className="bg-amber-600 hover:bg-amber-700 text-white text-xs sm:text-sm" data-testid="add-cred-btn"><Plus className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Nueva</span></Button>
                  <Button onClick={() => setShowCredenciales(null)} variant="ghost" className="text-slate-400"><X className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="p-3 sm:p-4 space-y-3 max-h-[70vh] overflow-y-auto">
                {credenciales.length === 0 && !credForm && (
                  <div className="text-center py-8"><Lock className="w-10 h-10 text-slate-600 mx-auto mb-3" /><p className="text-slate-400">Sin credenciales</p></div>
                )}
                {credForm && (
                  <div className="bg-arandu-dark border border-amber-500/20 rounded-lg p-3 sm:p-4 space-y-3">
                    <h3 className="text-amber-400 font-semibold text-sm">{credForm.id ? "Editar" : "Nueva"} Credencial</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Tipo de Acceso</label>
                        <select value={credForm.tipo_acceso} onChange={(e) => setCredForm({...credForm, tipo_acceso: e.target.value})} className="w-full bg-arandu-dark-light border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="cred-tipo">
                          <option value="">Seleccionar...</option>
                          <option value="SSH">SSH</option><option value="RDP">RDP</option><option value="Winbox">Winbox</option>
                          <option value="Panel Web">Panel Web</option><option value="FTP">FTP</option><option value="VPN">VPN</option>
                          <option value="WiFi">WiFi</option><option value="Base de Datos">Base de Datos</option>
                          <option value="NID">NID</option><option value="Otro">Otro</option>
                        </select>
                      </div>
                      <div><label className="text-slate-400 text-xs mb-1 block">Usuario / ID</label><Input value={credForm.usuario} onChange={(e) => setCredForm({...credForm, usuario: e.target.value})} className="bg-arandu-dark-light border-white/10 text-white" data-testid="cred-user" /></div>
                      <div><label className="text-slate-400 text-xs mb-1 block">Contrasena</label><Input value={credForm.password} onChange={(e) => setCredForm({...credForm, password: e.target.value})} className="bg-arandu-dark-light border-white/10 text-white" data-testid="cred-pass" /></div>
                      <div><label className="text-slate-400 text-xs mb-1 block">URL de Acceso</label><Input value={credForm.url_acceso} onChange={(e) => setCredForm({...credForm, url_acceso: e.target.value})} className="bg-arandu-dark-light border-white/10 text-white" data-testid="cred-url" /></div>
                      <div className="sm:col-span-2"><label className="text-slate-400 text-xs mb-1 block">Observaciones</label><Input value={credForm.observaciones} onChange={(e) => setCredForm({...credForm, observaciones: e.target.value})} className="bg-arandu-dark-light border-white/10 text-white" /></div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button onClick={() => setCredForm(null)} variant="ghost" className="text-slate-400 text-sm">Cancelar</Button>
                      <Button onClick={saveCredencial} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white text-sm" data-testid="save-cred-btn"><Save className="w-4 h-4 mr-1" /> Guardar</Button>
                    </div>
                  </div>
                )}
                {credenciales.map((cred, idx) => (
                  <div key={cred.id} className="bg-arandu-dark border border-white/5 rounded-lg p-3 sm:p-4 hover:border-amber-500/20 transition-all" data-testid={`cred-item-${idx}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <span className="text-amber-400 font-semibold text-xs sm:text-sm bg-amber-500/10 px-2 py-0.5 rounded">{cred.tipo_acceso || "Acceso"}</span>
                        {cred.url_acceso && <a href={cred.url_acceso} target="_blank" rel="noreferrer" className="text-cyan-400 text-xs hover:underline truncate max-w-[150px]">{cred.url_acceso}</a>}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => setCredForm({...cred})} className="p-1 text-slate-400 hover:text-amber-400"><Edit className="w-3.5 h-3.5" /></button>
                        <button onClick={() => deleteCredencial(cred.id)} className="p-1 text-slate-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                      <div>
                        <p className="text-slate-500 text-xs mb-0.5">Usuario / ID</p>
                        <div className="flex items-center gap-2"><p className="text-white text-sm font-mono truncate">{cred.usuario}</p><button onClick={() => copyToClipboard(cred.usuario)} className="text-slate-500 hover:text-cyan-400 flex-shrink-0"><Copy className="w-3 h-3" /></button></div>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs mb-0.5">Contrasena</p>
                        <div className="flex items-center gap-2">
                          <p className="text-white text-sm font-mono truncate">{showPasswords[cred.id] ? cred.password : "********"}</p>
                          <button onClick={() => setShowPasswords(p => ({...p, [cred.id]: !p[cred.id]}))} className="text-slate-500 hover:text-amber-400 flex-shrink-0" data-testid={`toggle-pass-${idx}`}>{showPasswords[cred.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}</button>
                          <button onClick={() => copyToClipboard(cred.password)} className="text-slate-500 hover:text-cyan-400 flex-shrink-0"><Copy className="w-3 h-3" /></button>
                        </div>
                      </div>
                    </div>
                    {cred.observaciones && <p className="text-slate-500 text-xs mt-2 italic">{cred.observaciones}</p>}
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== HISTORIAL MODAL ===== */}
      <AnimatePresence>
        {showHistorial && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-lg my-4 max-h-[85vh] overflow-y-auto">
              <div className="sticky top-0 bg-arandu-dark-light/95 backdrop-blur-sm border-b border-white/10 p-3 sm:p-4 flex justify-between items-center z-10">
                <h2 className="font-heading text-base sm:text-lg font-bold text-white flex items-center gap-2"><History className="w-5 h-5 text-purple-400" /> Historial</h2>
                <Button onClick={() => setShowHistorial(null)} variant="ghost" className="text-slate-400"><X className="w-4 h-4" /></Button>
              </div>
              <div className="p-3 sm:p-4 space-y-2">
                {historial.length === 0 ? <p className="text-slate-400 text-center py-6">Sin historial</p> : historial.map((h, idx) => (
                  <div key={h.id || idx} className="flex items-start gap-3 p-3 bg-arandu-dark rounded-lg border border-white/5">
                    <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center shrink-0"><Clock className="w-4 h-4 text-purple-400" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap"><span className="text-white text-sm font-medium">{h.usuario_nombre}</span><span className="text-purple-400 text-xs bg-purple-500/10 px-1.5 py-0.5 rounded">{h.accion}</span></div>
                      {h.detalle && <p className="text-slate-400 text-xs">{h.detalle}</p>}
                      <p className="text-slate-600 text-xs mt-1">{formatDate(h.fecha)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== CATEGORIAS MODAL ===== */}
      <AnimatePresence>
        {showCategorias && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-lg my-4 max-h-[85vh] overflow-y-auto">
              <div className="sticky top-0 bg-arandu-dark-light/95 backdrop-blur-sm border-b border-white/10 p-3 sm:p-4 flex justify-between items-center z-10">
                <h2 className="font-heading text-base sm:text-lg font-bold text-white flex items-center gap-2"><Settings className="w-5 h-5 text-purple-400" /> Categorias</h2>
                <Button onClick={() => setShowCategorias(false)} variant="ghost" className="text-slate-400"><X className="w-4 h-4" /></Button>
              </div>
              <div className="p-3 sm:p-4">
                <div className="bg-arandu-dark border border-white/10 rounded-lg p-3 mb-4">
                  <p className="text-slate-400 text-xs mb-2 font-semibold">Nueva Categoria</p>
                  <div className="flex gap-2">
                    <Input value={catForm.nombre} onChange={(e) => setCatForm({...catForm, nombre: e.target.value})} className="bg-arandu-dark-light border-white/10 text-white flex-1" placeholder="Nombre" data-testid="cat-name" />
                    <Button onClick={saveCat} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-cat-btn"><Plus className="w-4 h-4" /></Button>
                  </div>
                  {isAdmin && (
                    <label className="flex items-center gap-2 mt-2 text-xs text-slate-300 cursor-pointer select-none">
                      <input type="checkbox" checked={!!catForm.predeterminada} onChange={(e) => setCatForm({ ...catForm, predeterminada: e.target.checked })} className="accent-purple-500" />
                      Predeterminada para todo el sistema (se agrega a todas las empresas). Si no, solo para {activeEmpresaPropia?.nombre || "esta empresa"}.
                    </label>
                  )}
                </div>

                <p className="text-slate-500 text-[11px] mb-2">Estas categorías son de <strong className="text-slate-300">{activeEmpresaPropia?.nombre || "la empresa activa"}</strong>. Lo que cambies o elimines acá no afecta a las demás empresas.</p>
                {isAdmin && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    <Button type="button" onClick={recuperarTodo} className="bg-emerald-600/80 hover:bg-emerald-600 text-white text-xs h-8">Recuperar categorías perdidas</Button>
                    <Button type="button" onClick={() => { const v = !showPlantillas; setShowPlantillas(v); if (v) loadPlantillas(); }} className="bg-white/10 hover:bg-white/20 text-white text-xs h-8">{showPlantillas ? "Ocultar" : "Ver"} predeterminadas del sistema</Button>
                  </div>
                )}
                {isAdmin && showPlantillas && (
                  <div className="bg-arandu-dark border border-purple-500/30 rounded-lg p-3 mb-4">
                    <p className="text-purple-300 text-xs font-semibold mb-2">Predeterminadas del sistema (se copian a cada empresa nueva)</p>
                    {plantillasCat.length === 0 ? <p className="text-slate-500 text-xs">Sin predeterminadas.</p> : plantillasCat.map(t => (
                      <div key={t.id} className="flex items-center justify-between text-xs text-slate-300 py-1">
                        <span>{t.nombre} <span className="text-slate-500">({(t.subtipos || []).length} subtipos)</span></span>
                        <button onClick={() => deletePlantilla(t.id)} className="p-1 text-slate-500 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
                {isAdmin && categoriasEliminadas.length > 0 && (
                  <div className="bg-arandu-dark border border-amber-500/30 rounded-lg p-3 mb-4">
                    <p className="text-amber-300 text-xs font-semibold mb-2">Eliminadas en esta empresa</p>
                    {categoriasEliminadas.map(c => (
                      <div key={c.id} className="flex items-center justify-between text-xs text-slate-300 py-1">
                        <span>{c.nombre}</span>
                        <button onClick={() => restaurarCat(c.id)} className="text-emerald-400 hover:text-emerald-300 underline">Recuperar</button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="space-y-3">
                  {categorias.map(cat => (
                    <div key={cat.id} className="bg-arandu-dark border border-white/5 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-white font-medium text-sm">{cat.nombre}</p>
                        <button onClick={() => deleteCat(cat.id)} className="p-1 text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {(cat.subtipos || []).map((sub, si) => (
                          <span key={si} className="flex items-center gap-1 bg-arandu-dark-light text-slate-300 text-xs px-2 py-0.5 rounded border border-white/5">
                            {sub}
                            <button onClick={() => removeSubtipo(cat, si)} className="text-red-500/50 hover:text-red-400"><X className="w-2.5 h-2.5" /></button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-1">
                        <Input value={editingCat?.id === cat.id ? newSubtipo : ""} onChange={(e) => { setEditingCat(cat); setNewSubtipo(e.target.value); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSubtipoToCat(cat); } }}
                          className="bg-arandu-dark-light border-white/10 text-white flex-1 h-7 text-xs" placeholder="Nuevo subtipo + Enter" />
                        <Button type="button" onClick={() => addSubtipoToCat(cat)} className="bg-purple-600/50 hover:bg-purple-600 text-white h-7 text-xs px-2"><Plus className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InventarioPage;
